import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Heart, PlayCircle, Search, TrendingUp, Clock,
  ChevronLeft, ChevronRight, Star, User, Home,
  Film, Tv, X, BarChart3, Grid, Settings,
  Bell, Youtube, Music, ListPlus, Vote, ThumbsUp,
  ArrowUpCircle, Sparkles, AlertCircle, Loader,
  ChevronDown, Flame, Users, Award, Eye, Play, MessageSquare, Zap, Compass
} from 'lucide-react';
import api, { contentAPI, reactionAPI, voteAPI, featuredAPI, adminAPI, creatorAPI, peakMomentsAPI } from '../services/api';
import VideoEmbedModal from './VideoEmbedModal';
import PeakMomentsFeed from './PeakMomentsFeed';
import CommunityPeakMoments from './CommunityPeakMoments';
import CreatorDashboard from './Creator/CreatorDashboard';
import PublicCreatorProfile from './Creator/PublicCreatorProfile';
import VoteReactionsTab from './VoteReactionsTab';
import ThreadPreview from './ThreadPreview';
import Forum from './Forum';
import ThreadDetail from './ThreadDetail';
import CreateThreadModal from './CreateThreadModal';
import MySpaceContent from './MySpace/MySpaceContent';
import AuthModal from './AuthModal';
import { isAuthenticated, getUserFromToken, removeAuthToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Click tracking utility - fire-and-forget
const trackAndOpen = (channelId, url, source, mediaCategory) => {
  let clickType = 'internal';
  if (url) {
    if (source === 'peak_moments') {
      clickType = 'peak_moment_redirect';
    } else if (url.includes('/watch?v=') || url.includes('youtu.be/')) {
      clickType = 'video_redirect';
    } else {
      clickType = 'channel_redirect';
    }
  }
  if (channelId) creatorAPI.trackClick(channelId, source, null, null, clickType, mediaCategory || null);
  if (url) window.open(url, '_blank');
};

// Style constants
const colors = {
  black: '#000000',
  white: '#ffffff',
  gray: {
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
  purple: {
    400: '#A78BFA',
    500: '#8B5CF6',
    600: '#7C3AED',
    700: '#6D28D9',
  },
  pink: {
    500: '#EC4899',
  },
  green: {
    400: '#4ADE80',
    500: '#22C55E',
    600: '#16A34A',
  },
  yellow: {
    400: '#FACC15',
    500: '#EAB308',
  },
  red: {
    500: '#EF4444',
  },
  orange: {
    500: '#F97316',
    600: '#EA580C',
  }
};

const formatCompactNumber = (num) => {
  if (!num && num !== 0) return '';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return String(num);
};

// Skeleton loader component
const SkeletonCard = () => (
  <div style={{
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: colors.gray[800],
    animation: 'pulse 1.5s ease-in-out infinite',
    height: '150px'
  }}>
    <div style={{
      width: '100%',
      height: '100%',
      backgroundColor: colors.gray[700]
    }} />
  </div>
);

// Simple cache implementation
const contentCache = new Map();

// Custom hooks for data fetching
const useContent = (type = 'movie') => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const cacheKey = `content-${type}`;
    const cached = contentCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < 60 * 1000) {
      setData(cached.data);
      setLoading(false);
      return;
    }

    setLoading(true);
    contentAPI.getTrending(type)
      .then(response => {
        const shuffled = [...(response.results || [])].sort(() => Math.random() - 0.5);
        contentCache.set(cacheKey, { data: shuffled, timestamp: Date.now() });
        setData(shuffled);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, [type]);

  return { data, loading, error };
};

const useReactions = (contentId, options = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0, hasMore: false });
  const [searchParams, setSearchParams] = useState({ season: null, episode: null, reactorType: null });

  // Initial fetch - re-runs when contentId, season, episode, or reactorType changes
  useEffect(() => {
    if (!contentId) return;

    setLoading(true);
    setData([]);
    setPagination({ page: 1, totalPages: 0, total: 0, hasMore: false });

    const params = { page: 1, pageSize: 20 };
    if (options.season) params.season = options.season;
    if (options.episode) params.episode = options.episode;
    if (options.reactorType && options.reactorType !== 'all') params.reactorType = options.reactorType;
    setSearchParams({ season: options.season || null, episode: options.episode || null, reactorType: options.reactorType || null });

    reactionAPI.getReactions(contentId, params)
      .then(response => {
        console.log('Reactions response:', response);
        setData(response.reactions || []);
        setPagination({
          page: response.pagination?.page || 1,
          totalPages: response.pagination?.totalPages || 1,
          total: response.pagination?.total || response.reactions?.length || 0,
          hasMore: response.pagination?.hasMore || false
        });
      })
      .catch(error => {
        console.error('Error fetching reactions:', error);
        setError(error);
      })
      .finally(() => setLoading(false));
  }, [contentId, options.season, options.episode, options.reactorType]);

  // Load more function
  const loadMore = async () => {
    if (loadingMore || !pagination.hasMore) return;

    setLoadingMore(true);
    try {
      const nextPage = pagination.page + 1;
      const params = { page: nextPage, pageSize: 20 };
      if (searchParams.season) params.season = searchParams.season;
      if (searchParams.episode) params.episode = searchParams.episode;
      if (searchParams.reactorType && searchParams.reactorType !== 'all') params.reactorType = searchParams.reactorType;

      const response = await reactionAPI.getReactions(contentId, params);
      console.log('Load more response:', response);

      setData(prev => [...prev, ...(response.reactions || [])]);
      setPagination({
        page: response.pagination?.page || nextPage,
        totalPages: response.pagination?.totalPages || pagination.totalPages,
        total: response.pagination?.total || pagination.total,
        hasMore: response.pagination?.hasMore || false
      });
    } catch (error) {
      console.error('Error loading more reactions:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  return { data, loading, loadingMore, error, pagination, loadMore };
};

const useFeaturedChannels = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch ALL channels from the database (using reactions endpoint)
    api.get('/reactions/featured/channels?limit=100')
      .then(response => {
        const shuffled = [...(response.data.channels || [])].sort(() => Math.random() - 0.5);
        setData(shuffled);
      })
      .catch(async (error) => {
        try {
          const fallback = await featuredAPI.getFeaturedChannels();
          const shuffled = [...(fallback.channels || [])].sort(() => Math.random() - 0.5);
          setData(shuffled);
        } catch (_) {
          setError(error);
          setData([]);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  return { data, loading, error };
};

const useReactionOfWeek = (audience = 'general') => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    reactionAPI.getReactionOfWeek(audience)
      .then(response => {
        console.log('â­ Reaction of the Week response:', response);
        setData(response.videos || []);
      })
      .catch(error => {
        console.error('âŒ Error fetching Reaction of the Week:', error);
        setError(error);
        setData([]);
      })
      .finally(() => setLoading(false));
  }, []);

  return { data, loading, error };
};

// Modern Movie/TV Poster Card (Netflix-style)
const MoviePosterCard = ({ show, index, onSelect, type = 'movie' }) => {
  const [voted, setVoted] = useState(false);
  const [localVoteCount, setLocalVoteCount] = useState(show.voteCount || 0);
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);

  const handleVote = async (e) => {
    e.stopPropagation();
    try {
      await voteAPI.submitVote(show.id, type, 'reaction_request');
      setVoted(true);
      setLocalVoteCount(prev => prev + 1);
    } catch (error) {
      console.error('Failed to submit vote:', error);
      if (error.response?.data?.alreadyVoted) setVoted(true);
    }
  };

  const handleClick = () => {
    if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        onSelect(show, rect);
    } else {
        onSelect(show, {left: window.innerWidth / 2, top: window.innerHeight / 2, width: 0, height: 0});
    }
  };

  const getTypeGradient = () => {
    if (type === 'movie') return `linear-gradient(135deg, ${colors.purple[600]}, ${colors.purple[400]})`;
    if (type === 'series') return `linear-gradient(135deg, ${colors.green[600]}, ${colors.green[400]})`;
    if (type === 'music') return `linear-gradient(135deg, ${colors.yellow[500]}, ${colors.orange[500]})`;
    if (type === 'anime') return `linear-gradient(135deg, ${colors.pink[500]}, ${colors.red[500]})`;
    return `linear-gradient(135deg, ${colors.gray[600]}, ${colors.gray[400]})`;
  };

  return (
    <div
      ref={cardRef}
      className="movie-card"
      style={{
        position: 'relative',
        cursor: 'pointer',
        borderRadius: '12px',
        overflow: 'hidden',
        transform: isHovered ? 'translateY(-8px) scale(1.04)' : 'translateY(0) scale(1)',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        boxShadow: isHovered
          ? '0 16px 30px rgba(0,0,0,0.5)'
          : '0 8px 20px rgba(0,0,0,0.3)',
        minWidth: '150px',
        maxWidth: '150px',
        height: '214px'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      {/* Movie Poster */}
      {!imageError ? (
        <img
          src={show.posterUrl || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="180" height="270"%3E%3Crect fill="%23262626" width="180" height="270"/%3E%3C/svg%3E'}
          alt={show.title}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
          onError={() => setImageError(true)}
          loading="lazy"
        />
      ) : (
        <div style={{
          width: '100%',
          height: '100%',
          background: getTypeGradient(),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {type === 'series' ? <Tv style={{ width: '48px', height: '48px', color: colors.white }} /> :
           type === 'music' ? <Music style={{ width: '48px', height: '48px', color: colors.white }} /> :
           <Film style={{ width: '48px', height: '48px', color: colors.white }} />}
          <span style={{
            color: colors.white,
            fontSize: '14px',
            fontWeight: '600',
            textAlign: 'center',
            padding: '0 16px'
          }}>
            {show.title}
          </span>
        </div>
      )}

      {/* Hover Overlay */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 50%, transparent 100%)',
        padding: '16px 12px 12px',
        opacity: isHovered ? 1 : 0,
        transform: isHovered ? 'translateY(0)' : 'translateY(10px)',
        transition: 'all 0.3s ease',
        pointerEvents: isHovered ? 'auto' : 'none'
      }}>
        {/* Type Badge */}
        <div style={{
          display: 'inline-block',
          padding: '3px 8px',
          borderRadius: '6px',
          fontSize: '10px',
          fontWeight: '700',
          background: getTypeGradient(),
          color: colors.white,
          marginBottom: '6px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {type === 'series' ? 'TV Series' : type === 'music' ? 'Music' : type === 'anime' ? 'Anime' : type === 'dorama' ? 'Dorama' : 'Movie'}
        </div>

        <h3 style={{
          fontSize: '13px',
          fontWeight: '700',
          color: colors.white,
          marginBottom: '4px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          lineHeight: '1.3'
        }}>
          {show.title}
        </h3>

        <p style={{
          fontSize: '11px',
          color: colors.gray[400],
          marginBottom: '8px'
        }}>
          {type === 'music' ? `${show.artist || show.releaseYear || ''}` :
           (type === 'series' || type === 'anime' || type === 'dorama') ? `${show.seasonCount || show.seasons || 1} Seasons` : `${show.releaseYear || show.year || 2024}`}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Heart style={{ height: '12px', width: '12px', color: colors.purple[400], fill: voted ? colors.purple[400] : 'none' }} />
            <span style={{ fontSize: '11px', color: colors.white, fontWeight: '600' }}>{show.favoriteCount || 0}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <ThumbsUp style={{ height: '12px', width: '12px', color: colors.green[400] }} />
            <span style={{ fontSize: '11px', color: colors.white, fontWeight: '600' }}>{localVoteCount}</span>
          </div>
        </div>

        <button
          onClick={handleVote}
          style={{
            width: '100%',
            padding: '6px',
            borderRadius: '6px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: '700',
            transition: 'all 0.2s',
            background: voted ? colors.green[600] : getTypeGradient(),
            color: colors.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          <ThumbsUp size={14} />
          {voted ? 'Voted' : 'Vote'}
        </button>
      </div>
    </div>
  );
};

// Auto-scrolling Top Reactors Carousel
const TopReactorsCarousel = ({ onChannelClick }) => {
  const scrollRef = useRef(null);
  const [reactors, setReactors] = useState([]);

  // Fetch sidebar rankings (same data as Explore tab)
  useEffect(() => {
    const fetchReactors = async () => {
      try {
        const response = await featuredAPI.getSidebarRankings();
        if (response && response.success && response.rankings) {
          const rankings = response.rankings;
          const items = [];

          // Add Channel of the Week (Rank 1 - Gold)
          if (rankings.channelOfWeek) {
            items.push({
              channelId: rankings.channelOfWeek.channelId,
              channelName: rankings.channelOfWeek.name,
              thumbnail: rankings.channelOfWeek.thumbnail,
              rank: 1
            });
          }

          // Add Rising Star (Rank 2 - Silver)
          if (rankings.risingStar) {
            items.push({
              channelId: rankings.risingStar.channelId,
              channelName: rankings.risingStar.name,
              thumbnail: rankings.risingStar.thumbnail,
              rank: 2
            });
          }

          // Add Most Talked About (Rank 3 - Bronze)
          if (rankings.mostTalkedAbout) {
            items.push({
              channelId: rankings.mostTalkedAbout.channelId,
              channelName: rankings.mostTalkedAbout.name,
              thumbnail: rankings.mostTalkedAbout.thumbnail,
              rank: 3
            });
          }

          // Add Staff Pick (Rank 4 - Gray)
          if (rankings.staffPick) {
            items.push({
              channelId: rankings.staffPick.channelId,
              channelName: rankings.staffPick.name,
              thumbnail: rankings.staffPick.thumbnail,
              rank: 4
            });
          }

          if (items.length > 0) {
            setReactors(items);
            return;
          }
        }

        const fallback = await api.get('/reactions/featured/channels?limit=12');
        const fallbackItems = (fallback.data.channels || []).slice(0, 8).map((channel, index) => ({
          channelId: channel.id,
          channelName: channel.name || channel.title,
          thumbnail: channel.thumbnail || channel.avatar,
          rank: index + 1
        }));
        setReactors(fallbackItems);
      } catch (error) {
        console.error('Error fetching public reactors for carousel:', error);
      }
    };
    fetchReactors();
  }, []);

  // Show placeholder if no reactors yet
  if (reactors.length === 0) {
    return (
      <div style={{ marginBottom: '16px', overflow: 'hidden' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px'
        }}>
          <Star size={16} style={{ color: colors.yellow[400] }} />
          <span style={{ fontSize: '14px', fontWeight: '600', color: colors.white }}>Featured Reactors</span>
        </div>
        <div style={{
          padding: '24px',
          backgroundColor: 'rgba(255,255,255,0.02)',
          borderRadius: '10px',
          textAlign: 'center',
          border: '1px solid rgba(255,255,255,0.06)'
        }}>
          <p style={{ color: colors.gray[500], fontSize: '12px', margin: 0 }}>
            No featured reactors yet
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '4px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px'
      }}>
        <Star size={15} style={{ color: '#ffe45a' }} fill="#ffe45a" />
        <span style={{ fontSize: '15px', fontWeight: '700', color: colors.white }}>Featured Reactors</span>
      </div>

      <div
        ref={scrollRef}
        className="hide-scrollbar"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          paddingBottom: '2px'
        }}
      >
        {reactors.map((reactor, index) => (
          <ReactorTile key={`reactor-${index}`} reactor={reactor} onChannelClick={onChannelClick} />
        ))}
      </div>
    </div>
  );
};

// Reactor Tile Component - Modern Compact Design
const ReactorTile = ({ reactor, onChannelClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Get rank color based on rank number
  const getRankColor = (rank) => {
    if (rank === 1) return { bg: '#ffe45a', text: '#100c14' }; // Gold
    return { bg: 'rgba(255,255,255,0.1)', text: colors.white };
  };

  const rankColors = getRankColor(reactor.rank || 0);

  return (
    <div
      title={reactor.channelName}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '6px',
        cursor: 'pointer',
        flexShrink: 0
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        if (onChannelClick) {
          onChannelClick(reactor);
        } else if (reactor.channelId) {
          trackAndOpen(reactor.channelId, `https://youtube.com/channel/${reactor.channelId}`, 'sidebar');
        }
      }}
    >
      {/* Rank Badge — sits above the avatar */}
      <div style={{
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        backgroundColor: rankColors.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Space Mono', monospace",
        fontSize: '11px',
        fontWeight: '800',
        color: rankColors.text,
        flexShrink: 0
      }}>
        {reactor.rank}
      </div>

      {/* Channel Avatar */}
      <img
        src={reactor.thumbnail || '/api/placeholder/32/32'}
        alt={reactor.channelName}
        style={{
          width: '74px',
          height: '74px',
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
          border: `2px solid ${isHovered ? '#9b5cff' : 'rgba(255,255,255,0.12)'}`,
          transform: isHovered ? 'scale(1.08)' : 'scale(1)',
          transition: 'all 0.2s ease'
        }}
      />
    </div>
  );
};

// Compact strip of the first featured Peak Moments (home tab, next to Featured Reactors)
const FeaturedPeakMomentsStrip = ({ onPlay }) => {
  const [moments, setMoments] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await peakMomentsAPI.getFeaturedPeakMoments(3);
        let items = response.data || [];
        if (items.length === 0) {
          // No admin-featured moments yet — fall back to the newest approved ones
          const fallback = await peakMomentsAPI.getPeakMoments({ page: 1, limit: 3 });
          items = fallback.data || [];
        }
        setMoments(items.slice(0, 3));
      } catch (error) {
        console.error('Failed to load featured peak moments:', error);
      }
    };
    load();
  }, []);

  if (moments.length === 0) return null;

  return (
    <div style={{ marginBottom: '4px', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <Zap size={15} color="#ff8a3d" fill="#ff8a3d" />
        <span style={{ fontSize: '15px', fontWeight: '700', color: colors.white }}>Peak Moments</span>
      </div>
      <div
        className="hide-scrollbar"
        style={{ display: 'flex', gap: '12px', overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: '2px' }}
      >
        {moments.map((moment) => (
          <div
            key={moment._id || moment.id}
            onClick={() => onPlay(moment)}
            style={{
              flex: '1 1 0',
              minWidth: '150px',
              maxWidth: '210px',
              cursor: 'pointer',
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <div style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '16 / 9',
              borderRadius: '10px',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #241a33, #1a1322)',
              boxShadow: '0 6px 14px rgba(0,0,0,0.35)'
            }}>
              {moment.videoThumbnail && (
                <img
                  src={moment.videoThumbnail}
                  alt={moment.title || moment.videoTitle || 'Peak moment'}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(0deg, rgba(0,0,0,0.5), transparent 50%)'
              }} />
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                background: 'rgba(10,7,16,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Play size={13} color="#fff" fill="#fff" style={{ marginLeft: '2px' }} />
              </div>
            </div>
            <div style={{ marginTop: '6px', minWidth: 0 }}>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: colors.white,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {moment.title || moment.videoTitle || 'Peak moment'}
              </div>
              {moment.channelName && (
                <div style={{ fontSize: '11px', fontWeight: '500', color: 'rgba(255,255,255,0.5)', marginTop: '1px' }}>
                  {moment.channelName}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Right-sidebar trending row with tilted mini poster (straightens on hover)
const TrendingSideRow = ({ tag, tagColor, tilt, badgeNum, badgeBg, badgeColor, title, meta, posterUrl, onClick }) => {
  const posterRef = useRef(null);
  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '6px',
        borderRadius: '14px',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'transform 0.25s cubic-bezier(0.2,0.9,0.3,1.2), background 0.2s'
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateX(4px)';
        e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
        if (posterRef.current) posterRef.current.style.transform = 'rotate(0deg) scale(1.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateX(0)';
        e.currentTarget.style.background = 'transparent';
        if (posterRef.current) posterRef.current.style.transform = `rotate(${tilt})`;
      }}
    >
      <div
        ref={posterRef}
        style={{
          width: '78px',
          height: '106px',
          flexShrink: 0,
          borderRadius: '10px',
          position: 'relative',
          overflow: 'hidden',
          transform: `rotate(${tilt})`,
          boxShadow: '0 8px 16px rgba(0,0,0,0.4)',
          transition: 'transform 0.25s cubic-bezier(0.2,0.9,0.3,1.2)',
          background: 'linear-gradient(135deg, #241a33, #1a1322)'
        }}
      >
        {posterUrl && (
          <img
            src={posterUrl}
            alt={title}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(0deg, rgba(0,0,0,0.55), transparent 55%)'
        }} />
        <div style={{
          position: 'absolute',
          top: '5px',
          left: '5px',
          background: badgeBg,
          color: badgeColor,
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Space Mono', monospace",
          fontSize: '10px',
          fontWeight: '800'
        }}>
          {badgeNum}
        </div>
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontFamily: "'Space Mono', monospace",
          fontSize: '10px',
          fontWeight: '700',
          letterSpacing: '0.04em',
          color: tagColor,
          marginBottom: '3px'
        }}>
          {tag}
        </div>
        <div style={{
          fontSize: '14px',
          fontWeight: '700',
          color: colors.white,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {title}
        </div>
        {meta && (
          <div style={{ fontSize: '11px', fontWeight: '500', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
            {meta}
          </div>
        )}
      </div>
    </div>
  );
};

// Horizontal scrolling section with arrow navigation
const HorizontalScrollSection = ({ title, children, loading }) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const checkArrows = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 5);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };
  
  useEffect(() => {
    const currentRef = scrollRef.current;
    if (currentRef) {
        checkArrows();
        currentRef.addEventListener('scroll', checkArrows, { passive: true });
        const resizeObserver = new ResizeObserver(checkArrows);
        resizeObserver.observe(currentRef);

        return () => {
            currentRef.removeEventListener('scroll', checkArrows);
            resizeObserver.unobserve(currentRef);
        };
    }
  }, [children, loading]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth * 0.8;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div style={{ marginBottom: '13px' }}>
      <h2 style={{
        fontSize: '20px',
        fontWeight: '800',
        color: colors.white,
        marginBottom: '10px',
        letterSpacing: '-0.3px'
      }}>
        {title}
      </h2>

      <div style={{ position: 'relative' }}>
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            style={{
              position: 'absolute',
              left: '-20px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '48px',
              height: '48px',
              backgroundColor: 'rgba(0,0,0,0.8)',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 20,
              backdropFilter: 'blur(8px)',
              transition: 'all 0.3s'
            }}
          >
            <ChevronLeft style={{ width: '24px', height: '24px', color: colors.white }} />
          </button>
        )}

        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            style={{
              position: 'absolute',
              right: '-20px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '48px',
              height: '48px',
              backgroundColor: 'rgba(0,0,0,0.8)',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 20,
              backdropFilter: 'blur(8px)',
              transition: 'all 0.3s'
            }}
          >
            <ChevronRight style={{ width: '24px', height: '24px', color: colors.white }} />
          </button>
        )}

        <div
          ref={scrollRef}
          className="horizontal-scroll"
          style={{
            display: 'flex',
            gap: '16px',
            overflowX: 'auto',
            overflowY: 'hidden'
          }}
        >
          {loading ? (
            <>
              {[...Array(6)].map((_, i) => (
                <div key={i} style={{ minWidth: '260px' }}>
                  <SkeletonCard />
                </div>
              ))}
            </>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
};

// Carousel with Better Thumbnail Display
const FeaturedReactionsCarousel = ({ isMobile = false, audience = 'general', onMediaSelect }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [carouselData, setCarouselData] = useState([]);
  const [heroMediaPoster, setHeroMediaPoster] = useState(true);
  const [loading, setLoading] = useState(true);
  const mobileScrollRef = useRef(null);
  const scrollSettleTimer = useRef(null);
  const isAutoScrolling = useRef(false);
  const [embedVideoId, setEmbedVideoId] = useState(null);
  const [embedTitle, setEmbedTitle] = useState('');

  useEffect(() => {
    const loadCarouselData = async () => {
      try {
        setLoading(true);
        const response = await featuredAPI.getCarousel(audience);
        setCarouselData(response.carousel || []);
        setHeroMediaPoster(response.heroMediaPoster !== false);
      } catch (error) {
        console.error('Error loading carousel:', error);
        setCarouselData([
          {
            id: 1,
            title: "Inception Reactions",
            description: "Mind-bending thriller",
            image: "https://img.youtube.com/vi/YoHD9XEInc0/maxresdefault.jpg",
            imageUrl: "https://img.youtube.com/vi/YoHD9XEInc0/maxresdefault.jpg",
            channelName: "PopcornInBed",
            youtubeUrl: "https://www.youtube.com/watch?v=YoHD9XEInc0",
            stats: { reactions: 156, votes: 3420 }
          },
          {
            id: 2,
            title: "Breaking Bad Finale",
            description: "The most emotional reactions ever",
            image: "https://img.youtube.com/vi/UMKk6QKGh0s/maxresdefault.jpg",
            imageUrl: "https://img.youtube.com/vi/UMKk6QKGh0s/maxresdefault.jpg",
            channelName: "The Normies",
            youtubeUrl: "https://www.youtube.com/watch?v=UMKk6QKGh0s",
            stats: { reactions: 892, votes: 15600 }
          },
          {
            id: 3,
            title: "The Dark Knight",
            description: "Legendary villain reactions",
            image: "https://img.youtube.com/vi/kmJLuwP3MbY/maxresdefault.jpg",
            imageUrl: "https://img.youtube.com/vi/kmJLuwP3MbY/maxresdefault.jpg",
            channelName: "Natalie Gold",
            youtubeUrl: "https://www.youtube.com/watch?v=kmJLuwP3MbY",
            stats: { reactions: 245, votes: 8900 }
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadCarouselData();
  }, [audience]);

  const openEmbed = (slide) => {
    if (!slide?.youtubeUrl) return;
    const videoId = slide.youtubeUrl.match(/[?&]v=([^&]+)/)?.[1] || slide.youtubeUrl.match(/youtu\.be\/([^?]+)/)?.[1];
    if (!videoId) { trackAndOpen(slide.channelId, slide.youtubeUrl, 'home_featured'); return; }
    if (slide.channelId) creatorAPI.trackClick(slide.channelId, 'home_featured', null, null, 'video_embed_play');
    setEmbedTitle(slide.title || '');
    setEmbedVideoId(videoId);
  };

  useEffect(() => {
    if (!isPaused && !isHovered && carouselData.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => {
          const next = (prev + 1) % carouselData.length;
          if (mobileScrollRef.current) {
            isAutoScrolling.current = true;
            mobileScrollRef.current.scrollTo({ left: next * mobileScrollRef.current.clientWidth, behavior: 'smooth' });
            setTimeout(() => { isAutoScrolling.current = false; }, 700);
          }
          return next;
        });
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [currentSlide, isPaused, isHovered, carouselData.length]);

  // An empty carousel used to fall into this same branch, so whenever the API
  // returned zero slides the hero showed a loading spinner forever. Only spin
  // while actually loading; if there's genuinely nothing to show, render
  // nothing and let the rest of the page take over.
  if (!loading && carouselData.length === 0) return null;

  if (loading) {
    return (
      <div style={{
        height: isMobile ? '210px' : 'auto',
        aspectRatio: isMobile ? undefined : '2.7 / 1',
        backgroundColor: colors.gray[800],
        borderRadius: isMobile ? '16px' : '20px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Loader style={{ width: '32px', height: '32px', color: colors.purple[500], animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 10000);
  };

  const scrollToSlide = (index) => {
    if (mobileScrollRef.current) {
      isAutoScrolling.current = true;
      mobileScrollRef.current.scrollTo({ left: index * mobileScrollRef.current.clientWidth, behavior: 'smooth' });
      setTimeout(() => { isAutoScrolling.current = false; }, 700);
    }
  };

  const handleMobileScroll = () => {
    if (!mobileScrollRef.current || isAutoScrolling.current) return;
    clearTimeout(scrollSettleTimer.current);
    scrollSettleTimer.current = setTimeout(() => {
      const el = mobileScrollRef.current;
      if (!el || isAutoScrolling.current) return;
      const slideIndex = Math.round(el.scrollLeft / el.clientWidth);
      setCurrentSlide(slideIndex);
    }, 100);
  };

  const handleTouchStart = () => setIsPaused(true);
  const handleTouchEnd = () => setTimeout(() => setIsPaused(false), 8000);

  const getMaxResThumbnail = (slide) => {
    let thumbnailUrl = slide.imageUrl || slide.image || slide.videoThumbnail || slide.channelThumbnail || slide.thumbnail;
    
    if (slide.youtubeUrl) {
      const videoIdMatch = slide.youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      if (videoIdMatch) {
        thumbnailUrl = `https://img.youtube.com/vi/${videoIdMatch[1]}/maxresdefault.jpg`;
      }
    }
    
    if (thumbnailUrl && (thumbnailUrl.includes('youtube.com') || thumbnailUrl.includes('ytimg.com'))) {
      if (thumbnailUrl.includes('/vi/')) {
        const videoId = thumbnailUrl.match(/\/vi\/([^\/]+)/)?.[1];
        if (videoId) {
          thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        }
      }
    }
    
    return thumbnailUrl;
  };

  // Companion mode: show the media poster linked to the active reaction
  // instead of peeking the next slide (admin toggle, on by default)
  const activeSlideData = carouselData[currentSlide];
  const companionMedia = (!isMobile && heroMediaPoster && activeSlideData?.linkedMedia?.posterUrl)
    ? activeSlideData.linkedMedia
    : null;

  return (
    <>
    {embedVideoId && <VideoEmbedModal videoId={embedVideoId} title={embedTitle} onClose={() => setEmbedVideoId(null)} />}
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: isMobile ? '210px' : 'auto',
        aspectRatio: isMobile ? undefined : '2.7 / 1',
        marginBottom: isMobile ? '24px' : '14px',
        borderRadius: isMobile ? '16px' : '20px',
        overflow: 'hidden',
        cursor: isMobile ? 'pointer' : 'default',
        background: isMobile ? 'linear-gradient(120deg, #241a33, #170f21)' : 'transparent'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        ref={isMobile ? mobileScrollRef : undefined}
        className={isMobile ? 'hide-scrollbar' : undefined}
        onScroll={isMobile ? handleMobileScroll : undefined}
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          ...(isMobile ? {
            overflowX: 'scroll',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          } : {
            gap: '14px'
          })
        }}
      >
        {(isMobile
          ? carouselData.map((slide, index) => ({ slide, index }))
          : (carouselData.length > 1 && !companionMedia
              ? [currentSlide, (currentSlide + 1) % carouselData.length]
              : [currentSlide]
            ).map((i) => ({ slide: carouselData[i], index: i }))
        ).map(({ slide, index }) => {
          const thumbnailUrl = getMaxResThumbnail(slide);
          const isActive = index === currentSlide;
          const isPeek = !isMobile && !isActive;

          return (
            <div
              key={slide.id || index}
              onClick={isPeek ? () => goToSlide(index) : undefined}
              style={isMobile ? {
                position: 'relative',
                minWidth: '100%',
                height: '100%',
                scrollSnapAlign: 'start',
                background: `linear-gradient(135deg, ${colors.purple[600]}, ${colors.pink[500]})`
              } : {
                position: 'relative',
                height: '100%',
                flex: isActive ? '1 1 68%' : '1 1 32%',
                minWidth: 0,
                borderRadius: '20px',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'flex 0.4s cubic-bezier(0.2, 0.8, 0.3, 1)',
                background: 'linear-gradient(120deg, #241a33, #170f21)'
              }}
            >
              {isMobile ? (
                <>
                  {/* Mobile: full-height image with cinematic gradient overlay */}
                  <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: thumbnailUrl ? `url(${thumbnailUrl})` : `linear-gradient(135deg, ${colors.purple[600]}, ${colors.pink[500]})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center top'
                  }} />
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(180deg, rgba(0,0,0,0) 20%, rgba(0,0,0,0.65) 58%, rgba(0,0,0,0.96) 100%)'
                  }} />
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    padding: '10px 14px 14px',
                    zIndex: 2
                  }}>
                    <h1 style={{
                      fontSize: '13px',
                      fontWeight: '700',
                      color: colors.white,
                      margin: '0 0 5px 0',
                      lineHeight: '1.3',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      textOverflow: 'ellipsis',
                      textShadow: '0 1px 6px rgba(0,0,0,0.5)'
                    }}>
                      {slide.title}
                    </h1>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      {slide.channelName && (
                        <p style={{ fontSize: '11px', color: colors.purple[300], margin: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          by {slide.channelName}
                        </p>
                      )}
                      <button
                        onClick={() => openEmbed(slide)}
                        style={{
                          padding: '5px 12px',
                          backgroundColor: colors.purple[600],
                          color: colors.white,
                          border: 'none',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          flexShrink: 0,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.5)'
                        }}
                      >
                        <PlayCircle style={{ width: '10px', height: '10px' }} />
                        Watch
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Desktop: full-bleed thumbnail, top-biased framing so faces stay in shot */}
                  {thumbnailUrl && (
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      backgroundImage: `url(${thumbnailUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: isActive ? 'center 20%' : 'center',
                      backgroundRepeat: 'no-repeat',
                      opacity: 1
                    }} />
                  )}
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: isActive
                      ? 'linear-gradient(90deg, rgba(10,7,16,0.88) 0%, rgba(10,7,16,0.5) 22%, transparent 46%)'
                      : 'linear-gradient(90deg, rgba(10,7,16,0.92) 8%, rgba(10,7,16,0.45) 45%, transparent 75%)',
                    zIndex: 2
                  }} />
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(0deg, rgba(10,7,16,0.85) 0%, transparent 55%)',
                    zIndex: 2
                  }} />
                  {isActive && (
                  <div style={{
                    position: 'absolute',
                    left: '32px',
                    bottom: '22px',
                    right: '32px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '12px',
                    minWidth: 0,
                    zIndex: 3
                  }}>
                    <div style={{ minWidth: 0, width: '100%' }}>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontFamily: "'Space Mono', monospace",
                        fontSize: '11px',
                        fontWeight: '800',
                        letterSpacing: '0.06em',
                        color: '#100c14',
                        background: '#ffe45a',
                        padding: '5px 12px',
                        borderRadius: '100px',
                        marginBottom: '10px'
                      }}>
                        🔥 FEATURED
                      </div>
                      <h1 style={{
                        fontSize: '15px',
                        fontWeight: '700',
                        color: colors.white,
                        margin: '0 0 5px',
                        lineHeight: '1.35',
                        maxWidth: '440px',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        textOverflow: 'ellipsis',
                        textShadow: '0 1px 8px rgba(0,0,0,0.7)'
                      }}>
                        {slide.title}
                      </h1>
                      {slide.channelName && (
                        <div style={{ fontSize: '11px', fontWeight: '500', color: 'rgba(255,255,255,0.55)' }}>
                          Reaction video by {slide.channelName}
                        </div>
                      )}
                    </div>
                    <button
                      style={{
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'linear-gradient(135deg, #9b5cff, #ff5fa8)',
                        color: colors.white,
                        border: 'none',
                        fontSize: '13px',
                        fontWeight: '700',
                        padding: '11px 16px',
                        borderRadius: '100px',
                        cursor: 'pointer',
                        boxShadow: '0 10px 24px rgba(155,92,255,0.3)',
                        transition: 'transform 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      onClick={(e) => { e.stopPropagation(); openEmbed(slide); }}
                    >
                      <PlayCircle style={{ width: '16px', height: '16px' }} />
                      Watch Reaction
                    </button>
                  </div>
                  )}
                  {isActive && carouselData.length > 1 && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentSlide((prev) => (prev - 1 + carouselData.length) % carouselData.length);
                          setIsPaused(true);
                          setTimeout(() => setIsPaused(false), 10000);
                        }}
                        style={{
                          position: 'absolute',
                          left: '14px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '36px',
                          height: '36px',
                          backgroundColor: 'rgba(10,7,16,0.6)',
                          border: 'none',
                          borderRadius: '50%',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: isHovered ? 1 : 0,
                          transition: 'opacity 0.3s',
                          zIndex: 5
                        }}
                      >
                        <ChevronLeft style={{ width: '18px', height: '18px', color: colors.white }} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentSlide((prev) => (prev + 1) % carouselData.length);
                          setIsPaused(true);
                          setTimeout(() => setIsPaused(false), 10000);
                        }}
                        style={{
                          position: 'absolute',
                          right: '14px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '36px',
                          height: '36px',
                          backgroundColor: 'rgba(10,7,16,0.6)',
                          border: 'none',
                          borderRadius: '50%',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: isHovered ? 1 : 0,
                          transition: 'opacity 0.3s',
                          zIndex: 5
                        }}
                      >
                        <ChevronRight style={{ width: '18px', height: '18px', color: colors.white }} />
                      </button>
                    </>
                  )}
                  {isPeek && (
                    <>
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(10,7,16,0.35)',
                        zIndex: 3
                      }} />
                      <div style={{ position: 'absolute', left: '18px', bottom: '20px', right: '18px', zIndex: 4 }}>
                        <div style={{
                          fontSize: '15px',
                          fontWeight: '700',
                          lineHeight: '1.2',
                          color: colors.white,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {slide.title}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          );
        })}

        {!isMobile && companionMedia && (
          <div
            key={`companion-${activeSlideData?.id || currentSlide}`}
            style={{
              flex: '0 0 25%',
              minWidth: 0,
              height: '100%',
              marginLeft: '10px',
              paddingLeft: '20px',
              borderLeft: '1px solid rgba(255,255,255,0.08)',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Label OUTSIDE the card, above it */}
            <div style={{
              fontFamily: "'Space Mono', monospace",
              fontSize: '13px',
              fontWeight: '700',
              letterSpacing: '0.05em',
              color: '#ffe45a',
              margin: '0 2px 8px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexShrink: 0
            }}>
              SEE ALL REACTIONS ▸
            </div>
            <div
              onClick={() => onMediaSelect && onMediaSelect(companionMedia)}
              style={{
                flex: 1,
                minHeight: 0,
                borderRadius: '20px',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                background: 'linear-gradient(120deg, #241a33, #170f21)',
                boxShadow: '0 12px 28px rgba(0,0,0,0.45)'
              }}
              onMouseEnter={(e) => { const img = e.currentTarget.querySelector('img'); if (img) img.style.transform = 'scale(1.05)'; }}
              onMouseLeave={(e) => { const img = e.currentTarget.querySelector('img'); if (img) img.style.transform = 'scale(1)'; }}
            >
              <img
                src={companionMedia.posterUrl}
                alt={companionMedia.title}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.3s ease'
                }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg, rgba(0,0,0,0.65) 0%, transparent 45%)' }} />
              <div style={{ position: 'absolute', left: '16px', right: '16px', bottom: '14px' }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  lineHeight: '1.25',
                  color: colors.white,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {companionMedia.title}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={isMobile ? {
        position: 'absolute',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '6px',
        zIndex: 10
      } : {
        position: 'absolute',
        top: '20px',
        right: '24px',
        display: 'flex',
        gap: '6px',
        zIndex: 10
      }}>
        {carouselData.map((_, index) => (
          <button
            key={index}
            onClick={() => isMobile ? scrollToSlide(index) : goToSlide(index)}
            style={{
              width: currentSlide === index ? (isMobile ? '24px' : '20px') : (isMobile ? '8px' : '6px'),
              height: isMobile ? '8px' : '5px',
              backgroundColor: currentSlide === index
                ? (isMobile ? colors.white : '#ff5fa8')
                : (isMobile ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.25)'),
              border: 'none',
              borderRadius: isMobile ? '4px' : '3px',
              cursor: 'pointer',
              transition: 'all 0.3s',
              padding: 0
            }}
          />
        ))}
      </div>
    </div>
    </>
  );
};

// Channel Card Component
const ChannelCard = ({ channel, onChannelClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    else if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const handleChannelClick = () => {
    if (onChannelClick) {
      onChannelClick(channel);
      return;
    }
    if (channel) {
      if (channel.id && channel.id.startsWith('channel_')) {
        window.open(`https://www.youtube.com/results?search_query=${encodeURIComponent(channel.name || channel.id)}`, '_blank');
      } else if (channel.id) {
        trackAndOpen(channel.id, `https://www.youtube.com/channel/${channel.id}`, 'explore_tab');
      }
    }
  };

  return (
    <div
      style={{
        backgroundColor: colors.gray[900],
        borderRadius: '20px',
        overflow: 'hidden',
        cursor: 'pointer',
        transform: isHovered ? 'translateY(-8px) scale(1.03)' : 'translateY(0) scale(1)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: isHovered ? '0 15px 40px rgba(0,0,0,0.4)' : '0 6px 15px rgba(0,0,0,0.25)',
        minWidth: '210px',
        position: 'relative'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleChannelClick}
    >
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `linear-gradient(135deg, ${colors.purple[600]}, ${colors.pink[500]}, ${colors.purple[400]})`,
        backgroundSize: '300% 300%',
        animation: isHovered ? 'gradientShift 3s ease infinite' : 'gradientShift 6s ease infinite'
      }} />

      <div style={{
        height: '120px',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        <img
          src={channel.thumbnail || '/api/placeholder/140/140'}
          alt={channel.title || channel.name}
          style={{
            width: '90px',
            height: '90px',
            borderRadius: '50%',
            border: `4px solid ${colors.white}`,
            objectFit: 'cover',
            boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
            zIndex: 2,
            position: 'relative'
          }}
        />
        {(channel.primaryCategory === 'music-performance' || channel.primaryCategory === 'music' || channel.categories?.includes('music-performance') || channel.categories?.includes('music')) && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: 'rgba(234,179,8,0.15)',
            border: '1px solid rgba(234,179,8,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3
          }}>
            <Music size={11} color="#eab308" />
          </div>
        )}
      </div>

      <div style={{ 
        padding: '16px',
        position: 'relative',
        zIndex: 2,
        backgroundColor: colors.gray[900]
      }}>
        <h3 style={{
          fontSize: '16px',
          fontWeight: 'bold',
          color: colors.white,
          marginBottom: '12px',
          textAlign: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {channel.title || channel.name}
        </h3>

        <div style={{
          display: 'flex',
          justifyContent: 'space-around',
          marginTop: '12px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '18px', fontWeight: 'bold', color: colors.purple[400] }}>
              {formatNumber(channel.statistics?.subscriberCount || 0)}
            </p>
            <p style={{ fontSize: '10px', color: colors.gray[400], marginTop: '2px' }}>
              Subscribers
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '18px', fontWeight: 'bold', color: colors.green[400] }}>
              {formatNumber(channel.statistics?.videoCount || 0)}
            </p>
            <p style={{ fontSize: '10px', color: colors.gray[400], marginTop: '2px' }}>
              Videos
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Shared embed modal — renders via portal to escape overflow:hidden / transform ancestors
// YouTube Reactor Card
const ReactorVideoCard = ({ reactor, onStartThread, onViewThread, contentInfo }) => {
  const [voted, setVoted] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [embedVideoId, setEmbedVideoId] = useState(null);

  // Track boost impression once on mount if this card is from a boosted channel
  useEffect(() => {
    if (reactor?.isBoosted && reactor?.channelId) {
      creatorAPI.trackBoostImpression(reactor.channelId);
    }
  }, [reactor?.channelId, reactor?.isBoosted]);

  const handleVideoClick = (e) => {
    if (reactor && reactor.id) {
      if (reactor.channelId) {
        creatorAPI.trackClick(
          reactor.channelId, 'content_detail', reactor.id, null,
          'video_embed_play', contentInfo?.mediaCategory || null,
          contentInfo?.id ? String(contentInfo.id) : null,
          contentInfo?.title || null,
          contentInfo?.contentType || null
        );
      }
      setEmbedVideoId(reactor.id);
    }
  };

  const handleVoteClick = async (e) => {
    e.stopPropagation();
    try {
      // Vote for the reaction video itself
      await voteAPI.submitVote(reactor.id, 'reaction_video', 'reaction_request', reactor.title, reactor.thumbnail);
      setVoted(true);
      console.log('âœ… Vote submitted for reaction video');
    } catch (error) {
      console.error('âŒ Failed to submit vote for reaction video:', error);
      if (error.response?.data?.alreadyVoted) {
        setVoted(true);
      }
    }
  };

  return (
    <>
    {embedVideoId && <VideoEmbedModal videoId={embedVideoId} title={reactor?.title} onClose={() => setEmbedVideoId(null)} />}
    <div
      style={{
        borderRadius: '14px',
        overflow: 'hidden',
        backgroundColor: colors.gray[900],
        boxShadow: isHovered ? '0 12px 32px rgba(0,0,0,0.5)' : '0 4px 16px rgba(0,0,0,0.3)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleVideoClick}
    >
      {/* Thumbnail â€” always 16/9 */}
      <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', overflow: 'hidden', flexShrink: 0 }}>
        {!imageError ? (
          <img
            src={reactor.thumbnail}
            alt={reactor.title}
            style={{
              width: '100%', height: '100%', objectFit: 'cover', display: 'block',
              transition: 'transform 0.4s ease',
              transform: isHovered ? 'scale(1.06)' : 'scale(1)'
            }}
            onError={() => setImageError(true)}
            loading="lazy"
          />
        ) : (
          <div style={{ width: '100%', height: '100%', backgroundColor: colors.gray[800], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Youtube style={{ width: '40px', height: '40px', color: colors.gray[600] }} />
          </div>
        )}

        {/* Channel badge top-left */}
        <div style={{
          position: 'absolute', top: '8px', left: '8px',
          display: 'flex', alignItems: 'center', gap: '4px',
          background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)',
          borderRadius: '6px', padding: '3px 7px', maxWidth: '65%'
        }}>
          <Youtube size={10} style={{ color: colors.red[500], flexShrink: 0 }} />
          <span style={{ fontSize: '10px', color: colors.gray[200], fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {reactor.channelTitle}
          </span>
          {reactor.isBoosted && (
            <span style={{ fontSize: '8px', fontWeight: '700', color: '#FBBF24', flexShrink: 0 }}>âš¡</span>
          )}
        </div>

        {/* Play button â€” shown on hover */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.25s'
        }}>
          <div style={{
            width: '44px', height: '44px',
            background: 'linear-gradient(135deg, #F97316, #EA580C)',
            borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(249,115,22,0.5)'
          }}>
            <Play size={18} fill="white" style={{ color: colors.white, marginLeft: '2px' }} />
          </div>
        </div>
      </div>

      {/* Text section */}
      <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <h3 style={{
          fontSize: '12px', fontWeight: '600', color: colors.white,
          margin: 0, lineHeight: '1.4',
          display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis'
        }}>
          {reactor.title}
        </h3>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <ArrowUpCircle size={11} style={{ color: colors.green[400] }} />
              <span style={{ fontSize: '11px', color: colors.gray[400], fontWeight: '500' }}>
                {reactor.voteCount || reactor.votes || 0}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <Eye size={11} style={{ color: colors.purple[400] }} />
              <span style={{ fontSize: '11px', color: colors.gray[400], fontWeight: '500' }}>
                {reactor.views || 0}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <ThreadPreview
              compact
              contentType="reaction_video"
              contentId={reactor.id}
              contentData={{ title: reactor.title, thumbnail: reactor.thumbnail, channelName: reactor.channelTitle }}
              onStartThread={onStartThread}
              onViewThread={onViewThread}
            />

            <button
              onClick={handleVoteClick}
              style={{
                padding: '4px 10px',
                backgroundColor: voted ? colors.green[600] : 'rgba(34,197,94,0.1)',
                border: `1px solid ${voted ? colors.green[600] : colors.green[500]}`,
                borderRadius: '6px',
                color: voted ? colors.white : colors.green[400],
                cursor: 'pointer', fontSize: '11px', fontWeight: '600',
                transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', gap: '4px'
              }}
            >
              <ThumbsUp size={10} fill={voted ? 'white' : 'none'} />
              {voted ? 'Voted!' : 'Vote'}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

// Search Component - Modern Compact Design
const SearchComponent = ({ onSearch, loading }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchActive, setSearchActive] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchTimeoutRef = useRef(null);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    else if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const performSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setSearchActive(false);
      return;
    }

    setSearchLoading(true);
    try {
      const [dbResults, channelResults] = await Promise.all([
        contentAPI.searchContent(query).then(data => data.results || []).catch(() => []),
        featuredAPI.getFeaturedChannels().then(data =>
          data.channels?.filter(channel =>
            channel.name?.toLowerCase().includes(query.toLowerCase()) ||
            channel.title?.toLowerCase().includes(query.toLowerCase())
          ).slice(0, 5) || []
        ).catch(() => []),
      ]);

      setSearchResults([
        ...dbResults.slice(0, 8),
        ...channelResults.map(item => ({ ...item, type: 'channel' }))
      ]);
      setSearchActive(true);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);
  };

  const handleResultClick = (result) => {
    if (result.type === 'channel') {
      trackAndOpen(result.id, `https://www.youtube.com/channel/${result.id}`, 'search_result');
    } else {
      onSearch(result, { type: result.type });
    }
    setSearchActive(false);
    setSearchQuery('');
  };

  return (
    <div style={{ marginBottom: '20px', position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search style={{
          position: 'absolute',
          left: '20px',
          top: '50%',
          transform: 'translateY(-50%)',
          height: '16px',
          width: '16px',
          color: isFocused ? colors.purple[400] : 'rgba(255,255,255,0.35)',
          transition: 'color 0.2s',
          zIndex: 1
        }} />
        <input
          type="text"
          placeholder="Search movies, TV shows, music, or reactors..."
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          style={{
            width: '100%',
            padding: '14px 40px 14px 46px',
            backgroundColor: '#120d1a',
            border: `1px solid ${isFocused ? 'rgba(155, 92, 255, 0.45)' : 'rgba(255,255,255,0.08)'}`,
            borderRadius: '100px',
            color: colors.white,
            fontSize: '14px',
            fontWeight: '500',
            outline: 'none',
            transition: 'all 0.2s',
            boxSizing: 'border-box'
          }}
        />

        {searchLoading && (
          <div style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }}>
            <Loader style={{ width: '16px', height: '16px', color: colors.purple[500], animation: 'spin 1s linear infinite' }} />
          </div>
        )}
      </div>

      {searchActive && searchResults.length > 0 && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          left: 0,
          right: 0,
          backgroundColor: '#111827',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
          zIndex: 50,
          maxHeight: '360px',
          overflowY: 'auto'
        }}>
          <div style={{
            padding: '10px 14px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <Search size={12} color={colors.gray[500]} />
            <span style={{ color: colors.gray[400], fontSize: '11px', fontWeight: '500' }}>
              {searchResults.length} results
            </span>
          </div>

          <div style={{ padding: '6px' }}>
            {searchResults.map((result, index) => (
              <div
                key={`${result.type}-${result.id || index}`}
                onClick={() => handleResultClick(result)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <img
                  src={result.posterUrl || result.thumbnail || '/api/placeholder/36/36'}
                  alt={result.title || result.name}
                  style={{
                    width: '36px',
                    height: result.type === 'channel' ? '36px' : '50px',
                    borderRadius: result.type === 'channel' ? '50%' : '6px',
                    objectFit: 'cover',
                    flexShrink: 0
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    color: colors.white,
                    fontSize: '13px',
                    fontWeight: '500',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {result.title || result.name}
                  </div>
                  <div style={{ color: colors.gray[500], fontSize: '11px', marginTop: '2px' }}>
                    {result.type === 'channel'
                      ? `${formatNumber(result.statistics?.subscriberCount || 0)} subscribers`
                      : result.type === 'music'
                      ? `Music â€¢ ${result.artist || result.releaseYear || ''}`
                      : `${result.type.charAt(0).toUpperCase() + result.type.slice(1)} â€¢ ${result.releaseYear || ''}`}
                  </div>
                </div>
                <div style={{
                  padding: '3px 8px',
                  backgroundColor:
                    result.type === 'movie' ? 'rgba(139, 92, 246, 0.2)' :
                    result.type === 'music' ? 'rgba(234, 179, 8, 0.2)' :
                    result.type === 'anime' ? 'rgba(249, 115, 22, 0.2)' :
                    result.type === 'channel' ? 'rgba(34, 197, 94, 0.2)' :
                    'rgba(96, 165, 250, 0.2)',
                  color:
                    result.type === 'movie' ? colors.purple[400] :
                    result.type === 'music' ? colors.yellow[400] :
                    result.type === 'anime' ? colors.orange[400] :
                    result.type === 'channel' ? colors.green[400] :
                    colors.blue[400],
                  borderRadius: '6px',
                  fontSize: '10px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  flexShrink: 0
                }}>
                  {result.type === 'channel' ? 'Channel' : result.type.charAt(0).toUpperCase() + result.type.slice(1)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// MODERN: Split-pane Content Details with side-by-side layout
const ContentDetailsOverlay = ({ content, position, onClose, onSeasonSelect, onStartThread, onViewThread, onFavorited }) => {
  const [animating, setAnimating] = useState(true);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [showSeasons, setShowSeasons] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [localFavoriteCount, setLocalFavoriteCount] = useState(content?.favoriteCount || 0);
  const [filterReactorType, setFilterReactorType] = useState('all');

  // Season/Episode filter for TV shows and anime
  const [filterSeason, setFilterSeason] = useState(null);
  const [filterEpisode, setFilterEpisode] = useState(null);
  const isTvOrAnime =
    content?.type === 'series' ||
    content?.type === 'anime' ||
    content?.type === 'dorama' ||
    content?.seasons > 0 ||
    content?.genres?.some(g =>
      typeof g === 'string'
        ? (g.toLowerCase().includes('anime') || g.toLowerCase().includes('animation'))
        : (g?.name?.toLowerCase().includes('animation') || g?.name?.toLowerCase().includes('anime'))
    );

  const reactionSearchTitle = buildReactionSearchTitle(content);

  const { data: reactions, loading: reactionsLoading, loadingMore, pagination, loadMore } = useReactions(
    reactionSearchTitle,
    { season: filterSeason, episode: filterEpisode, reactorType: filterReactorType }
  );
  const overlayRef = useRef(null);

  const seasons = content?.seasons || content?.number_of_seasons || 10;
  const episodes = content?.episodes || content?.number_of_episodes || 30;
  const [customSeason, setCustomSeason] = useState(false);
  const [customEpisode, setCustomEpisode] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimating(false), 50);
    if (overlayRef.current) {
        overlayRef.current.focus();
    }
    
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      clearTimeout(timer);
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (!animating && content) {
        document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
        document.removeEventListener('keydown', handleKeyDown);
    };
  }, [animating, content, onClose]);

  // Check if content is already favorited
  useEffect(() => {
    const checkIfFavorited = async () => {
      try {
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        if (!token || !content) return;

        const response = await axios.get(`${API_URL}/myspace/dashboard`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data.success) {
          const contentLikes = response.data.data.contentLikes || [];
          const contentType = content.type || 'series';
          const isAlreadyFavorited = contentLikes.some(
            like => like.contentId === String(content.dbId || content.id) && like.contentType === contentType
          );
          setIsFavorited(isAlreadyFavorited);
        }
      } catch (error) {
        console.error('âŒ Failed to check if favorited:', error);
      }
    };

    checkIfFavorited();
  }, [content]);

  if (!content) return null;

  const isMobile = window.innerWidth <= 768;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.85)',
        zIndex: 100,
        display: 'flex',
        alignItems: window.innerWidth <= 768 ? 'stretch' : 'center',
        justifyContent: window.innerWidth <= 768 ? 'stretch' : 'center',
        backdropFilter: 'blur(10px)'
      }}
      onClick={onClose}
    >
      <div
        ref={overlayRef}
        tabIndex="-1"
        style={{
          position: 'relative',
          width: isMobile ? '100%' : '95%',
          maxWidth: isMobile ? 'none' : '1400px',
          height: isMobile ? '100%' : '90vh',
          backgroundColor: colors.gray[900],
          borderRadius: isMobile ? 0 : '24px',
          overflow: 'hidden',
          transform: animating ? `scale(0.5)` : 'scale(1)',
          opacity: animating ? 0 : 1,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          transformOrigin: position ? `${position.x}px ${position.y}px` : 'center center',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: 'rgba(0,0,0,0.8)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 30,
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s'
          }}
        >
          <X style={{ width: '18px', height: '18px', color: colors.white }} />
        </button>

        {/* LEFT PANE: Content Details */}
        <div style={{
          width: isMobile ? '100%' : '32%',
          height: isMobile ? 'auto' : '100%',
          flexShrink: 0,
          background: `linear-gradient(135deg, ${colors.gray[900]} 0%, ${colors.gray[800]} 100%)`,
          borderRight: isMobile ? 'none' : `1px solid ${colors.gray[700]}`,
          borderBottom: isMobile ? `1px solid ${colors.gray[700]}` : 'none',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            height: isMobile ? '200px' : '70%',
            position: 'relative',
            backgroundImage: `url(${content.posterUrl || '/api/placeholder/600/300'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            alignItems: 'flex-end',
            flexShrink: 0
          }}>
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to top, rgba(17, 24, 39, 0.95) 0%, rgba(17, 24, 39, 0.5) 50%, transparent 100%)'
            }} />
            <div style={{
              position: 'relative',
              padding: isMobile ? '10px 52px 10px 14px' : '16px 20px',
              width: '100%',
              zIndex: 2
            }}>
              <h1 style={{
                fontSize: isMobile ? '15px' : '22px',
                fontWeight: 'bold',
                color: colors.white,
                margin: '0 0 6px 0',
                textShadow: '0 2px 10px rgba(0,0,0,0.8)',
                lineHeight: '1.2',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}>
                {content.title}
              </h1>

              <div style={{
                display: 'flex',
                gap: '10px',
                marginTop: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '5px 12px',
                  backgroundColor: 'rgba(139, 92, 246, 0.2)',
                  borderRadius: '16px',
                  border: `1px solid ${colors.purple[500]}30`
                }}>
                  <Heart style={{ width: '14px', height: '14px', color: colors.purple[400] }} />
                  <span style={{ color: colors.white, fontSize: '13px', fontWeight: '600' }}>
                    {localFavoriteCount}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '5px 12px',
                  backgroundColor: 'rgba(34, 197, 94, 0.2)',
                  borderRadius: '16px',
                  border: `1px solid ${colors.green[500]}30`
                }}>
                  <ThumbsUp style={{ width: '14px', height: '14px', color: colors.green[400] }} />
                  <span style={{ color: colors.white, fontSize: '13px', fontWeight: '600' }}>
                    {content.voteCount || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div style={{
            ...(isMobile ? { flexShrink: 0 } : { flex: 1, overflowY: 'auto' }),
            padding: isMobile ? '10px 14px' : '16px 20px',
            display: 'flex',
            flexDirection: 'column'
          }}>

            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'row' : 'column',
              gap: '8px',
              marginTop: isMobile ? 0 : '12px'
            }}>
              <button
                onClick={async () => {
                  try {
                    const contentType = content.type || 'series';
                    const title = content.title || content.name;
                    const thumbnail = content.posterUrl || null;
                    await voteAPI.submitVote(content.dbId || content.id, contentType, 'reaction_request', title, thumbnail);
                    console.log('Vote submitted successfully');
                  } catch (error) {
                    console.error('Failed to submit vote:', error);
                  }
                }}
                style={{
                  width: isMobile ? 'auto' : '100%',
                  flex: isMobile ? 1 : undefined,
                  padding: isMobile ? '8px 6px' : '10px 16px',
                  backgroundColor: colors.green[600],
                  color: colors.white,
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: isMobile ? '11px' : '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? '4px' : '8px',
                  boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)'
                }}
              >
                <ThumbsUp style={{ width: '14px', height: '14px' }} />
                Vote for Reaction
              </button>

              <button
                onClick={async () => {
                  if (isFavorited) return;

                  try {
                    const posterUrl = content.posterUrl || null;
                    const contentType = content.type || 'series';

                    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
                    if (!token) {
                      alert('Please log in to add favorites');
                      return;
                    }

                    const payload = {
                      contentId: content.dbId || content.id,
                      contentType,
                      title: content.title || content.name,
                      poster: posterUrl
                    };
                    console.log('â¤ï¸ [favorite] sending:', payload);

                    const response = await axios.post(`${API_URL}/myspace/like-content`, payload, {
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                      }
                    });

                    console.log('âœ… [favorite] response:', response.data);
                    setIsFavorited(true);
                    setLocalFavoriteCount(prev => prev + 1);
                    onFavorited?.();
                  } catch (error) {
                    const msg = error.response?.data?.error || error.message || 'Unknown error';
                    console.error('âŒ [favorite] failed:', msg, error);
                    if (msg === 'Already liked this content') {
                      setIsFavorited(true);
                    } else {
                      alert('Failed to save favorite: ' + msg);
                    }
                  }
                }}
                style={{
                  width: isMobile ? 'auto' : '100%',
                  flex: isMobile ? 1 : undefined,
                  padding: isMobile ? '8px 6px' : '10px 16px',
                  backgroundColor: isFavorited ? colors.red[500] + '20' : 'transparent',
                  color: isFavorited ? colors.red[400] : colors.white,
                  border: `1.5px solid ${isFavorited ? colors.red[500] : colors.gray[600]}`,
                  borderRadius: '10px',
                  fontSize: isMobile ? '11px' : '13px',
                  fontWeight: '600',
                  cursor: isFavorited ? 'default' : 'pointer',
                  transition: 'all 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? '4px' : '8px',
                  opacity: isFavorited ? 0.7 : 1
                }}
              >
                <Heart style={{ width: '14px', height: '14px', fill: isFavorited ? colors.red[500] : 'none' }} />
                {isFavorited ? 'Favorited' : 'Add to Favorites'}
              </button>

              <button
                onClick={() => {
                  onStartThread({
                    type: content.type || 'series',
                    id: content.dbId || content.id,
                    title: content.title || content.name,
                    posterUrl: content.posterUrl || null
                  });
                }}
                style={{
                  width: isMobile ? 'auto' : '100%',
                  flex: isMobile ? 1 : undefined,
                  padding: isMobile ? '8px 6px' : '10px 16px',
                  backgroundColor: 'transparent',
                  color: colors.purple[400],
                  border: `1.5px solid ${colors.purple[500]}`,
                  borderRadius: '10px',
                  fontSize: isMobile ? '11px' : '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? '4px' : '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.purple[500] + '20';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Users style={{ width: '14px', height: '14px' }} />
                Start Thread
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT PANE: Reactions */}
        <div style={{
          flex: 1,
          minWidth: 0,
          height: isMobile ? 'auto' : '100%',
          minHeight: isMobile ? 0 : undefined,
          backgroundColor: colors.gray[900],
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '16px 20px 12px 20px',
            borderBottom: `1px solid ${colors.gray[700]}`
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
              <div>
                <h2 style={{
                  fontSize: '15px',
                  fontWeight: 'bold',
                  color: colors.white,
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Youtube style={{ width: '17px', height: '17px', color: colors.red[500] }} />
                  Reaction Videos
                </h2>
                <p style={{
                  color: colors.gray[400],
                  fontSize: '10px',
                  margin: '3px 0 0 25px'
                }}>
                  {reactionsLoading ? 'Loading reactions...' :
                   reactions?.length ? `${reactions.length} reactions available` :
                   'No reactions found yet'}
                </p>
              </div>
              {!isMobile && (
                <p style={{
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.3)',
                  margin: '0 40px 0 0',
                  fontStyle: 'italic',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}>
                  Found a creator through ReactorHub? Let them know their viewers are discovering them here.
                </p>
              )}
            </div>

            {/* Reactor Type Filter */}
            <div style={{
              display: 'flex',
              gap: '6px',
              marginTop: '10px',
              flexWrap: isMobile ? 'nowrap' : 'wrap',
              overflowX: isMobile ? 'auto' : 'visible',
              scrollbarWidth: 'none',
              paddingBottom: '2px'
            }}>
              {[
                { key: 'all', label: 'All', emoji: '', color: colors.purple[500] },
                { key: 'solo', label: 'Solo Vibes', emoji: '🎯', color: '#8B5CF6' },
                { key: 'duo', label: 'Reacting Together', emoji: '💑', color: '#EC4899' },
                { key: 'group', label: 'Group Energy', emoji: '👥', color: '#F97316' },
                { key: 'family', label: 'Family Feels', emoji: '👨‍👩‍👧‍👦', color: '#10B981' }
              ].map(type => (
                <button
                  key={type.key}
                  onClick={(e) => { e.stopPropagation(); setFilterReactorType(type.key); }}
                  style={{
                    padding: '5px 10px',
                    fontSize: '11px',
                    fontWeight: '600',
                    border: 'none',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    backgroundColor: filterReactorType === type.key ? type.color : colors.gray[800],
                    color: filterReactorType === type.key ? '#fff' : colors.gray[400]
                  }}
                >
                  {type.emoji ? `${type.emoji} ` : ''}{type.label}
                </button>
              ))}
            </div>
          </div>
          
          <div style={{
            flex: 1,
            padding: '16px',
            overflowY: 'auto'
          }}>
            {/* Season/Episode Filter for TV Shows and Anime */}
            {isTvOrAnime && (
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: colors.gray[800],
                borderRadius: '12px',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <span style={{ color: colors.gray[300], fontSize: '14px', fontWeight: '500' }}>
                  Filter by:
                </span>

                {/* Season Selector */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ color: colors.gray[400], fontSize: '13px' }}>Season</label>
                  {customSeason ? (
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={filterSeason || ''}
                      onChange={(e) => {
                        const val = e.target.value ? parseInt(e.target.value) : null;
                        setFilterSeason(val);
                        if (!val) setFilterEpisode(null);
                      }}
                      placeholder="S#"
                      style={{
                        padding: '8px 12px',
                        backgroundColor: colors.gray[700],
                        color: colors.white,
                        border: `1px solid ${colors.gray[600]}`,
                        borderRadius: '8px',
                        fontSize: '14px',
                        width: '60px'
                      }}
                    />
                  ) : (
                    <select
                      value={filterSeason || ''}
                      onChange={(e) => {
                        if (e.target.value === 'custom') {
                          setCustomSeason(true);
                          setFilterSeason(null);
                        } else {
                          const val = e.target.value ? parseInt(e.target.value) : null;
                          setFilterSeason(val);
                          if (!val) setFilterEpisode(null);
                        }
                      }}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: colors.gray[700],
                        color: colors.white,
                        border: `1px solid ${colors.gray[600]}`,
                        borderRadius: '8px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        minWidth: '80px'
                      }}
                    >
                      <option value="">All</option>
                      {[...Array(10)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>S{i + 1}</option>
                      ))}
                      <option value="custom">Other...</option>
                    </select>
                  )}
                  {customSeason && (
                    <button
                      onClick={() => { setCustomSeason(false); setFilterSeason(null); }}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: colors.gray[600],
                        color: colors.gray[300],
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >Ã—</button>
                  )}
                </div>

                {/* Episode Selector */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ color: colors.gray[400], fontSize: '13px' }}>Episode</label>
                    {customEpisode ? (
                      <input
                        type="number"
                        min="1"
                        max="999"
                        value={filterEpisode || ''}
                        onChange={(e) => setFilterEpisode(e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="E#"
                        style={{
                          padding: '8px 12px',
                          backgroundColor: colors.gray[700],
                          color: colors.white,
                          border: `1px solid ${colors.gray[600]}`,
                          borderRadius: '8px',
                          fontSize: '14px',
                          width: '60px'
                        }}
                      />
                    ) : (
                      <select
                        value={filterEpisode || ''}
                        onChange={(e) => {
                          if (e.target.value === 'custom') {
                            setCustomEpisode(true);
                            setFilterEpisode(null);
                          } else {
                            setFilterEpisode(e.target.value ? parseInt(e.target.value) : null);
                          }
                        }}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: colors.gray[700],
                          color: colors.white,
                          border: `1px solid ${colors.gray[600]}`,
                          borderRadius: '8px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          minWidth: '80px'
                        }}
                      >
                        <option value="">All</option>
                        {[...Array(10)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>E{i + 1}</option>
                        ))}
                        <option value="custom">Other...</option>
                      </select>
                    )}
                    {customEpisode && (
                      <button
                        onClick={() => { setCustomEpisode(false); setFilterEpisode(null); }}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: colors.gray[600],
                          color: colors.gray[300],
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >Ã—</button>
                    )}
                </div>

                {/* Clear Filter Button */}
                {(filterSeason || filterEpisode) && (
                  <button
                    onClick={() => {
                      setFilterSeason(null);
                      setFilterEpisode(null);
                      setCustomSeason(false);
                      setCustomEpisode(false);
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      color: colors.purple[400],
                      border: `1px solid ${colors.purple[500]}`,
                      borderRadius: '8px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.purple[500];
                      e.currentTarget.style.color = colors.white;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = colors.purple[400];
                    }}
                  >
                    Clear Filter
                  </button>
                )}

                {/* Current filter display */}
                {filterSeason && (
                  <span style={{
                    marginLeft: 'auto',
                    padding: '6px 12px',
                    backgroundColor: colors.purple[600],
                    color: colors.white,
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    Searching: S{String(filterSeason).padStart(2, '0')}
                    {filterEpisode ? `E${String(filterEpisode).padStart(2, '0')}` : ''}
                  </span>
                )}
              </div>
            )}

            {reactionsLoading ? (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                gap: '20px' 
              }}>
                {[...Array(6)].map((_, i) => (
                  <div key={i} style={{
                    borderRadius: '16px', 
                    backgroundColor: colors.gray[800], 
                    animation: 'pulse 1.5s ease-in-out infinite', 
                    height: '240px'
                  }}/>
                ))}
              </div>
            ) : reactions && reactions.length > 0 ? (
              <>
                {/* Reactions count header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <span style={{ color: colors.gray[400], fontSize: '14px' }}>
                    Showing {reactions.length} of {pagination.total} reactions
                  </span>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: '14px'
                }}>
                  {reactions.map((reactor) => (
                    <ReactorVideoCard
                      key={reactor.id}
                      reactor={reactor}
                      onStartThread={onStartThread}
                      onViewThread={onViewThread}
                      contentInfo={{
                        id: content?.id,
                        title: content?.title,
                        contentType: content?.type === 'music' ? 'music'
                          : content?.type === 'anime' ? 'anime'
                          : isTvOrAnime ? 'series'
                          : 'movie',
                        mediaCategory: isTvOrAnime
                          ? (content?.genres?.some(g => (g?.name || g)?.toLowerCase?.().includes('anime')) ? 'anime' : 'tv-shows')
                          : content?.type === 'music' ? 'music-performance'
                          : 'movies',
                      }}
                    />
                  ))}
                </div>

                {/* Load More Button */}
                {pagination.hasMore && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '24px'
                  }}>
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      style={{
                        padding: '12px 32px',
                        backgroundColor: loadingMore ? colors.gray[700] : colors.purple[600],
                        color: colors.white,
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: loadingMore ? 'wait' : 'pointer',
                        transition: 'all 0.3s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        if (!loadingMore) e.currentTarget.style.backgroundColor = colors.purple[500];
                      }}
                      onMouseLeave={(e) => {
                        if (!loadingMore) e.currentTarget.style.backgroundColor = colors.purple[600];
                      }}
                    >
                      {loadingMore ? (
                        <>
                          <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                          Loading...
                        </>
                      ) : (
                        <>
                          Load More ({pagination.total - reactions.length} remaining)
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                textAlign: 'center',
                padding: '32px'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: colors.gray[800],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px'
                }}>
                  <Youtube style={{
                    width: '40px',
                    height: '40px',
                    color: colors.gray[500]
                  }} />
                </div>
                <h3 style={{
                  color: colors.white,
                  fontSize: '18px',
                  fontWeight: 'bold',
                  marginBottom: '8px'
                }}>
                  No Reactions Yet
                </h3>
                <p style={{
                  color: colors.gray[400],
                  fontSize: '13px',
                  lineHeight: '1.5',
                  marginBottom: '16px'
                }}>
                  Be the first to request a reaction for this content!
                </p>
                <button style={{
                  padding: '10px 20px',
                  backgroundColor: colors.purple[600],
                  color: colors.white,
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <Vote style={{ width: '14px', height: '14px' }} />
                  Request Reaction
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Sidebar Component
const SIDEBAR_ICON_PATHS = {
  home: <path d="M218.83,103.77l-80-75.48a20,20,0,0,0-27.66,0l-80,75.48A20,20,0,0,0,25,119V208a20,20,0,0,0,20,20H92a12,12,0,0,0,12-12V160a24,24,0,0,1,48,0v56a12,12,0,0,0,12,12h47a20,20,0,0,0,20-20V119A20,20,0,0,0,218.83,103.77Z"/>,
  explore: <path d="M208,32H48A24,24,0,0,0,24,56V200a24,24,0,0,0,24,24H208a24,24,0,0,0,24-24V56A24,24,0,0,0,208,32ZM96,180a12,12,0,1,1,12-12A12,12,0,0,1,96,180Zm0-56a12,12,0,1,1,12-12A12,12,0,0,1,96,124Zm64,56a12,12,0,1,1,12-12A12,12,0,0,1,160,180Zm0-56a12,12,0,1,1,12-12A12,12,0,0,1,160,124Z"/>,
  peak: <path d="M197.58,129.06,146,110l-19-51.62a15.92,15.92,0,0,0-29.88,0L78,110l-51.62,19a15.92,15.92,0,0,0,0,29.88L78,178l19,51.62a15.92,15.92,0,0,0,29.88,0L146,178l51.62-19a15.92,15.92,0,0,0,0-29.88Z"/>,
  myspace: <path d="M240,102c0,70-103.79,126.66-108.21,129a8,8,0,0,1-7.58,0C119.79,228.66,16,172,16,102A62.07,62.07,0,0,1,78,40c20.65,0,38.73,8.88,50,23.89C139.27,48.88,157.35,40,178,40A62.07,62.07,0,0,1,240,102Z"/>,
  music: <path d="M212,32a12,12,0,0,0-9.72-2.32l-120,20A12,12,0,0,0,72,61.72V166.14A36,36,0,1,0,96,200V93.94l104-17.33v57.53A36,36,0,1,0,224,168V44A12,12,0,0,0,212,32Z"/>,
  vote: <path d="M234,80.12A24,24,0,0,0,216,72H160V56a40,40,0,0,0-40-40,8,8,0,0,0-7.16,4.42L75.06,96H32a16,16,0,0,0-16,16v88a16,16,0,0,0,16,16H204a24,24,0,0,0,23.82-21l12-88A24,24,0,0,0,234,80.12Z"/>,
  forum: <path d="M128,24A104,104,0,0,0,36.18,176.88L24.83,210.93a16,16,0,0,0,20.24,20.24l34.05-11.35A104,104,0,1,0,128,24Z"/>,
};

const SidebarIcon = ({ name, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 256 256" fill="currentColor">{SIDEBAR_ICON_PATHS[name]}</svg>
);

const Sidebar = ({ activeTab, setActiveTab, collapsed, setCollapsed, currentUser, setShowAuthModal, handleLogout, onPlayFeed }) => {
  const [signOutHover, setSignOutHover] = useState(false);
  const [viewProfileHover, setViewProfileHover] = useState(false);

  const discoverItems = [
    { id: 'home', icon: 'home', label: 'Home' },
    { id: 'explore', icon: 'explore', label: 'Explore' },
    { id: 'peak-moments', icon: 'peak', label: 'Peak Moments', badge: 'HOT' },
  ];
  const communityItems = [
    { id: 'favorites', icon: 'myspace', label: 'MySpace' },
    { id: 'music', icon: 'music', label: 'Music' },
    { id: 'votes', icon: 'vote', label: 'Vote Reactions' },
    { id: 'forum', icon: 'forum', label: 'Forum' },
  ];

  const itemBaseStyle = (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '10px',
    padding: '10px 10px',
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: "'Manrope', sans-serif",
    transition: 'background 0.15s ease, color 0.15s ease',
    justifyContent: collapsed ? 'center' : 'flex-start',
    background: isActive
      ? 'linear-gradient(135deg, rgba(139,92,246,0.22), rgba(236,72,153,0.16))'
      : 'transparent',
    color: isActive ? '#fff' : '#a8a5b4',
    boxShadow: isActive ? 'inset 0 0 0 1px rgba(139,92,246,0.35)' : 'none',
  });

  const renderNavButton = (item) => {
    const isActive = activeTab === item.id;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => setActiveTab(item.id)}
        title={item.label}
        style={itemBaseStyle(isActive)}
        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: isActive ? '#e879f9' : '#8f8b9c' }}>
          {item.icon === 'peak' ? <Flame size={18} /> : item.icon === 'explore' ? <Compass size={18} /> : <SidebarIcon name={item.icon} />}
        </span>
        {!collapsed && (
          <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.label}
          </span>
        )}
        {item.badge && !collapsed && (
          <span style={{ background: '#dc2626', color: '#fff', fontSize: '9.5px', fontWeight: 800, letterSpacing: '0.04em', padding: '2px 7px', borderRadius: '999px', flexShrink: 0 }}>
            {item.badge}
          </span>
        )}
      </button>
    );
  };

  const creatorActive = activeTab === 'creator';
  const userName = currentUser?.username || currentUser?.name || 'User';
  const userInitial = userName ? userName[0].toUpperCase() : 'U';

  return (
  <div style={{
    position: 'fixed',
    left: 0,
    top: 0,
    height: '100%',
    width: collapsed ? '76px' : '256px',
    flexShrink: 0,
    background: '#0d0c12',
    borderRight: '1px solid #1c1a26',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.22s ease',
    zIndex: 40,
    fontFamily: "'Manrope', sans-serif",
  }}>
    {/* Header: real logo + name art, adapted to the compact horizontal placement */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: collapsed ? '22px 10px 16px' : '22px 18px 16px', position: 'relative', justifyContent: collapsed ? 'center' : 'flex-start' }}>
      <img
        src="/assets/brand/logo-nobg.png"
        alt="ReactorHub"
        style={{ width: '40px', height: '40px', objectFit: 'contain', flexShrink: 0 }}
      />
      {!collapsed && (
        <div style={{ minWidth: 0 }}>
          {/* The wordmark's letterforms occupy only 963x72px inside a 1536x1024 canvas —
              the rest is transparent padding and glow. Render the full image scaled up
              inside a tight window so only the lettering shows, with no dead space
              pushing BETA away from it. Offsets are derived from the measured bounds. */}
          <div style={{ width: '150px', height: '14px', overflow: 'hidden', position: 'relative' }}>
            <img
              src="/assets/brand/reactorhub_brand_nameart.png"
              alt="ReactorHub"
              style={{ width: '235px', height: 'auto', display: 'block', position: 'absolute', left: '-43px', top: '-67px', maxWidth: 'none' }}
            />
          </div>
          <div style={{ fontSize: '10px', fontWeight: 800, letterSpacing: '0.12em', color: '#f59e0b', marginTop: '5px' }}>
            BETA
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        style={{
          position: 'absolute', right: '-11px', top: '24px',
          width: '22px', height: '22px', borderRadius: '50%',
          background: '#181722', border: '1px solid #2a2836', color: '#a8a5b4',
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          transform: `rotate(${collapsed ? 180 : 0}deg)`, transition: 'transform 0.2s ease',
          zIndex: 1,
        }}
      >
        <svg width="11" height="11" viewBox="0 0 256 256" fill="currentColor"><path d="M165.66,202.34a8,8,0,0,1-11.32,11.32l-80-80a8,8,0,0,1,0-11.32l80-80a8,8,0,0,1,11.32,11.32L91.31,128Z"/></svg>
      </button>
    </div>

    <div style={{ height: '1px', background: '#1c1a26', margin: '0 18px 12px' }} />

    {/* Navigation */}
    <nav style={{ flex: 1, overflowY: 'auto', padding: '0 12px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <style>{`
        @keyframes pf-flow { 0% { background-position: 0% 50%; } 100% { background-position: 300% 50%; } }
        @keyframes pf-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(249,115,22,0.5), 0 6px 18px rgba(236,72,153,0.32); }
          50%      { box-shadow: 0 0 0 7px rgba(249,115,22,0), 0 8px 24px rgba(236,72,153,0.5); }
        }
        @keyframes pf-eq { 0%,100% { transform: scaleY(0.4); } 50% { transform: scaleY(1); } }
        .pf-btn:hover { transform: translateY(-2px) scale(1.02); }
        .pf-btn { transition: transform 0.16s ease; }
      `}</style>

      <button
        type="button"
        className="pf-btn"
        onClick={onPlayFeed}
        title="Play Peak Moments feed"
        style={{
          display: 'flex', alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: '10px', width: '100%',
          padding: collapsed ? '11px' : '11px 13px',
          margin: '4px 0 14px',
          borderRadius: '12px', border: 'none', cursor: 'pointer',
          background: 'linear-gradient(110deg, #f97316, #ec4899, #8b5cf6, #f97316)',
          backgroundSize: '300% 100%',
          animation: 'pf-flow 7s linear infinite, pf-glow 2.8s ease-in-out infinite',
          color: '#fff', fontFamily: "'Manrope', sans-serif",
          fontSize: '14px', fontWeight: 800,
        }}
      >
        <span style={{
          width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
          background: 'rgba(255,255,255,0.24)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Play size={12} fill="#fff" color="#fff" />
        </span>
        {!collapsed && (
          <>
            <span style={{ flex: 1, textAlign: 'left', whiteSpace: 'nowrap' }}>Play Moments</span>
            {/* little equaliser so the button reads as "something is playing" */}
            <span style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '13px', flexShrink: 0 }}>
              {[0, 0.25, 0.5].map((d, i) => (
                <span key={i} style={{
                  width: '3px', height: '13px', borderRadius: '2px',
                  background: 'rgba(255,255,255,0.85)',
                  transformOrigin: 'bottom',
                  animation: `pf-eq 0.9s ease-in-out ${d}s infinite`,
                }} />
              ))}
            </span>
          </>
        )}
      </button>

      {!collapsed && (
        <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: '#4c485a', padding: '8px 10px 6px' }}>DISCOVER</div>
      )}
      {discoverItems.map(renderNavButton)}

      {!collapsed
        ? <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: '#4c485a', padding: '16px 10px 6px' }}>COMMUNITY</div>
        : <div style={{ height: '10px' }} />
      }
      {communityItems.map(renderNavButton)}

      {!collapsed
        ? <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: '#4c485a', padding: '16px 10px 6px' }}>CREATE</div>
        : <div style={{ height: '10px' }} />
      }
      <button
        type="button"
        onClick={() => {
          if (!currentUser) { setShowAuthModal(true); return; }
          setActiveTab('creator');
        }}
        title="Creator Studio"
        style={itemBaseStyle(creatorActive)}
        onMouseEnter={(e) => { if (!creatorActive) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
        onMouseLeave={(e) => { if (!creatorActive) e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: creatorActive ? '#e879f9' : '#8f8b9c' }}>
          <svg width="18" height="18" viewBox="0 0 256 256" fill="currentColor">
            <path d="M164.44,105.34l-48,32A8,8,0,0,1,104,130.66V66.66a8,8,0,0,1,12.44-6.68l48,32a8,8,0,0,1,0,13.32Z"/>
            <path opacity="0.3" d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,160H40V56H216V200Z"/>
          </svg>
        </span>
        {!collapsed && (
          <span style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
              <span>Creator Studio</span>
              <span style={{ background: 'linear-gradient(135deg,#8b5cf6,#ec4899)', color: '#fff', fontSize: '9.5px', fontWeight: 800, letterSpacing: '0.04em', padding: '2px 7px', borderRadius: '999px' }}>BETA</span>
            </div>
            <div style={{ fontSize: '11px', color: '#6f6b7d', fontWeight: 500, marginTop: '1px' }}>Join the creator whitelist</div>
          </span>
        )}
      </button>

    </nav>

    {/* Footer */}
    <div style={{ padding: '12px', borderTop: '1px solid #1c1a26', display: 'flex', flexDirection: 'column', gap: '10px', flexShrink: 0 }}>
      {/* Lives in the footer rather than the scrolling nav so it is never clipped.
          Deliberately quiet -- the loud support CTA is the one in the home feed. */}
      <button
        type="button"
        onClick={openSupportPage}
        title="Support ReactorHub"
        style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'center',
          gap: '8px', width: '100%', flexShrink: 0,
          padding: collapsed ? '9px' : '9px 12px',
          borderRadius: '10px', cursor: 'pointer',
          background: 'transparent',
          border: '1px solid #232130',
          color: '#c9c6d3', fontFamily: "'Manrope', sans-serif",
          fontSize: '13px', fontWeight: 600,
          transition: 'border-color 0.15s ease, color 0.15s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'rgba(249,115,22,0.5)';
          e.currentTarget.style.color = '#fdba74';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#232130';
          e.currentTarget.style.color = '#c9c6d3';
        }}
      >
        <Heart size={15} style={{ flexShrink: 0 }} />
        {!collapsed && <span style={{ whiteSpace: 'nowrap' }}>Support us</span>}
      </button>

      {!collapsed && currentUser && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#141018', border: '1px solid #232130', borderRadius: '12px', padding: '9px 10px' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg,#8b5cf6,#ec4899)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: '13px', color: '#fff', flexShrink: 0 }}>
            {userInitial}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#f4f3f7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</div>
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setActiveTab('favorites'); }}
              onMouseEnter={() => setViewProfileHover(true)}
              onMouseLeave={() => setViewProfileHover(false)}
              style={{ fontSize: '11.5px', fontWeight: 600, color: viewProfileHover ? '#e879f9' : '#c084fc', textDecoration: 'none' }}
            >
              View profile
            </a>
          </div>
        </div>
      )}

      {!collapsed && !currentUser && (
        <button
          type="button"
          onClick={() => setShowAuthModal(true)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            background: '#ffffff', color: '#141018', padding: '10px 14px', borderRadius: '10px',
            border: 'none', fontWeight: 700, fontSize: '13.5px', cursor: 'pointer', transition: 'opacity 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
        >
          <svg width="16" height="16" viewBox="0 0 18 18">
            <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
            <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
            <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
            <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
          </svg>
          Sign in with Google
        </button>
      )}

      {collapsed && !currentUser && (
        <button
          type="button"
          onClick={() => setShowAuthModal(true)}
          title="Sign in with Google"
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#ffffff', padding: '10px', borderRadius: '10px', border: 'none', cursor: 'pointer',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 18 18">
            <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
            <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
            <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
            <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
          </svg>
        </button>
      )}

      {currentUser && (
        <button
          type="button"
          onClick={handleLogout}
          onMouseEnter={() => setSignOutHover(true)}
          onMouseLeave={() => setSignOutHover(false)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            background: '#141018', border: `1px solid ${signOutHover ? '#ec4899' : '#232130'}`,
            color: signOutHover ? '#f9a8d4' : '#c9c6d3',
            borderRadius: '10px', padding: '10px 14px', fontSize: '13.5px', fontWeight: 600,
            fontFamily: "'Manrope', sans-serif", cursor: 'pointer',
            transition: 'border-color 0.15s ease, color 0.15s ease',
          }}
        >
          <svg width="15" height="15" viewBox="0 0 256 256" fill="currentColor" style={{ flexShrink: 0 }}>
            <path d="M124,216a12,12,0,0,1-12,12H48a12,12,0,0,1-12-12V40A12,12,0,0,1,48,28h64a12,12,0,0,1,0,24H60V204h52A12,12,0,0,1,124,216Zm113.66-96.49-40-40a12,12,0,0,0-17,17L204.28,120H112a12,12,0,0,0,0,24h92.28l-23.6,23.51a12,12,0,0,0,17,17l40-40A12,12,0,0,0,237.66,119.51Z"/>
          </svg>
          {!collapsed && <span>Sign out</span>}
        </button>
      )}
    </div>
  </div>
  );
};

const HomeSignupPrompt = ({ isMobile, currentUser, onSignup }) => {
  if (currentUser) return null;

  return (
    <div style={{
      display: 'flex',
      alignItems: isMobile ? 'stretch' : 'center',
      justifyContent: 'space-between',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '12px' : '18px',
      padding: isMobile ? '14px' : '16px 18px',
      marginBottom: isMobile ? '14px' : '18px',
      borderRadius: '12px',
      border: '1px solid rgba(139, 92, 246, 0.28)',
      background: 'linear-gradient(135deg, rgba(139,92,246,0.18), rgba(249,115,22,0.10))',
      boxShadow: '0 14px 32px rgba(0,0,0,0.22)'
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{
          color: colors.white,
          fontSize: isMobile ? '15px' : '16px',
          fontWeight: 800,
          lineHeight: 1.25,
          marginBottom: '4px'
        }}>
          Join ReactorHub beta
        </div>
        <div style={{
          color: 'rgba(255,255,255,0.68)',
          fontSize: isMobile ? '12px' : '13px',
          lineHeight: 1.45
        }}>
          Save your favorite reactors, vote on what gets watched next, and join the forum.
        </div>
      </div>
      <button
        onClick={onSignup}
        style={{
          flexShrink: 0,
          width: isMobile ? '100%' : 'auto',
          minHeight: '42px',
          padding: '10px 16px',
          borderRadius: '10px',
          border: 'none',
          background: 'linear-gradient(135deg, #8B5CF6, #F97316)',
          color: colors.white,
          fontSize: '14px',
          fontWeight: 800,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          boxShadow: '0 10px 22px rgba(139, 92, 246, 0.28)'
        }}
      >
        <User style={{ width: '16px', height: '16px' }} />
        Sign up free
      </button>
    </div>
  );
};

const SUPPORT_URL = 'https://buy.stripe.com/9B68wR7351SP7jUc07f3a00';

const openSupportPage = () => {
  window.open(SUPPORT_URL, '_blank', 'noopener,noreferrer');
};

// Standalone support card. Deliberately placed well down the home feed, away from
// the signup banner, so the two calls to action never share the same screen block.
// Kept to a single compact row -- it should read as a strip, not a hero block.
const SupportBanner = ({ isMobile }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div style={{
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      alignItems: isMobile ? 'stretch' : 'center',
      justifyContent: 'space-between',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '10px' : '20px',
      padding: isMobile ? '12px 14px' : '10px 20px',
      margin: isMobile ? '14px 0 16px' : '14px 0 18px',
      borderRadius: '12px',
      border: '1px solid rgba(249, 115, 22, 0.38)',
      background: 'linear-gradient(135deg, rgba(249,115,22,0.18), rgba(236,72,153,0.14) 55%, rgba(139,92,246,0.16))',
      boxShadow: '0 10px 26px rgba(0,0,0,0.28)'
    }}>
      {/* warm glow anchoring the left edge, purely decorative */}
      <div style={{
        position: 'absolute',
        left: '-50px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(249,115,22,0.28), transparent 68%)',
        pointerEvents: 'none'
      }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, position: 'relative' }}>
        <div style={{
          width: '34px',
          height: '34px',
          borderRadius: '10px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #F97316, #EC4899)',
          boxShadow: '0 6px 16px rgba(236,72,153,0.34)'
        }}>
          <Heart style={{ width: '17px', height: '17px', color: '#fff' }} fill="#fff" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            color: colors.white,
            fontSize: isMobile ? '14px' : '15px',
            fontWeight: 800,
            lineHeight: 1.25
          }}>
            Help us bring you more features
          </div>
          <div style={{
            color: 'rgba(255,255,255,0.66)',
            fontSize: isMobile ? '12px' : '12.5px',
            lineHeight: 1.35,
            marginTop: '1px'
          }}>
            No ads, no paywalls. We run on servers, coffee, and people who love watching reactions.
          </div>
        </div>
      </div>

      <button
        onClick={openSupportPage}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          position: 'relative',
          flexShrink: 0,
          width: isMobile ? '100%' : 'auto',
          minHeight: '36px',
          padding: '8px 18px',
          borderRadius: '9px',
          border: 'none',
          background: 'linear-gradient(135deg, #F97316, #EC4899)',
          color: colors.white,
          fontSize: '13.5px',
          fontWeight: 800,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          whiteSpace: 'nowrap',
          transform: hovered ? 'translateY(-1px)' : 'none',
          boxShadow: hovered
            ? '0 12px 24px rgba(249, 115, 22, 0.44)'
            : '0 8px 18px rgba(249, 115, 22, 0.30)',
          transition: 'transform 0.16s ease, box-shadow 0.16s ease'
        }}
      >
        <Heart style={{ width: '15px', height: '15px' }} fill="#fff" />
        Support ReactorHub
      </button>
    </div>
  );
};

const FeedModeTabs = ({ value, onChange, options, compact = false }) => (
  <div style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: compact ? '14px' : '16px',
    overflowX: 'auto',
    maxWidth: '100%'
  }}>
    {options.map((option) => {
      const active = value === option.id;
      return (
        <button
          key={option.id}
          onClick={option.disabled ? undefined : () => onChange(option.id)}
          style={{
            border: 'none',
            cursor: option.disabled ? 'not-allowed' : 'pointer',
            padding: compact ? '9px 14px' : '9px 20px',
            borderRadius: '100px',
            background: active ? 'linear-gradient(135deg, #9b5cff, #ff5fa8)' : 'transparent',
            color: option.disabled ? 'rgba(255,255,255,0.35)' : active ? colors.white : 'rgba(255,255,255,0.68)',
            fontSize: compact ? '12px' : '13px',
            fontWeight: active ? '700' : '600',
            whiteSpace: 'nowrap',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {option.label}
          {option.disabled && (
            <span style={{
              fontFamily: "'Space Mono', monospace",
              fontSize: '9px',
              fontWeight: '700',
              color: 'rgba(255,255,255,0.45)',
              background: 'rgba(255,255,255,0.08)',
              borderRadius: '5px',
              padding: '2px 7px',
              letterSpacing: '0.04em',
              textTransform: 'uppercase'
            }}>In Development</span>
          )}
        </button>
      );
    })}
  </div>
);

const ONBOARDING_STORAGE_KEY = 'reactorhub_onboarding_dismissed_v1';
const ONBOARDING_TITLES = [
  {
    title: 'Back to the Future',
    searchAliases: ['Back to the Future'],
    fallback: {
      id: 'onboarding-back-to-the-future',
      dbId: 'onboarding-back-to-the-future',
      title: 'Back to the Future',
      releaseYear: 1985,
      posterUrl: 'https://image.tmdb.org/t/p/w500/7lyBcpYB0Qt8gYhXYaEZUNlAvOe.jpg'
    }
  },
  {
    title: 'Interstellar',
    searchAliases: ['Interstellar'],
    fallback: {
      id: 'onboarding-interstellar',
      dbId: 'onboarding-interstellar',
      title: 'Interstellar',
      releaseYear: 2014,
      posterUrl: 'https://image.tmdb.org/t/p/w500/gEU2QniE6E77NI6lCU6MxlNBvIx.jpg'
    }
  },
  {
    title: 'The Lord of the Rings: The Fellowship of the Ring',
    searchAliases: [
      'The Lord of the Rings: The Fellowship of the Ring',
      'Lord of the Rings: The Fellowship of the Ring',
      'Lord of the Rings Fellowship of the Ring',
      'LOTR Fellowship of the Ring',
      'The Fellowship of the Ring'
    ],
    fallback: {
      id: 'onboarding-lotr-fellowship',
      dbId: 'onboarding-lotr-fellowship',
      title: 'The Lord of the Rings: The Fellowship of the Ring',
      releaseYear: 2001,
      posterUrl: 'https://image.tmdb.org/t/p/w500/6oom5QYQ2yQTMJIbnvbkBL9cHo6.jpg'
    }
  }
];

const normalizeTitle = (value = '') => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const pickBestOnboardingMatch = (items = [], title) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  const normalizedTarget = normalizeTitle(title);
  const exact = items.find((item) => normalizeTitle(item.title || '') === normalizedTarget);
  if (exact) return exact;

  const withoutLeadingArticle = normalizedTarget.replace(/^the\s+/, '');
  const articleAware = items.find((item) => {
    const normalizedItem = normalizeTitle(item.title || '');
    const itemWithoutLeadingArticle = normalizedItem.replace(/^the\s+/, '');
    return normalizedItem.includes(normalizedTarget) ||
      normalizedTarget.includes(normalizedItem) ||
      itemWithoutLeadingArticle.includes(withoutLeadingArticle) ||
      withoutLeadingArticle.includes(itemWithoutLeadingArticle);
  });

  return articleAware || null;
};

const buildReactionSearchTitle = (content) => {
  const FORCE_YEAR_TITLES = [
    'click', 'up', 'her', 'us', 'it', 'heat', 'vice', 'glass', 'split', 'wild',
    'get out', 'the help', 'the house', 'the visit', 'the gift', 'the call',
    'the hunt', 'the invite', 'the room', 'the menu', 'run', 'live', 'fall', 'fresh'
  ];

  const title = content?.title || '';
  const year = content?.releaseYear || content?.year ||
    content?.release_date?.slice(0, 4) || content?.first_air_date?.slice(0, 4) || '';
  const trimmed = title.trim();
  const isSingleWord = trimmed.split(/\s+/).filter(Boolean).length === 1;
  const isNumeric = /^\d+$/.test(trimmed);

  if (year && FORCE_YEAR_TITLES.includes(trimmed.toLowerCase())) {
    return `${title} ${year}`;
  }
  if (isSingleWord && year && !isNumeric && trimmed.length <= 3) {
    return `${title} ${year}`;
  }

  return title;
};

const FirstVisitOnboarding = ({
  isMobile,
  items,
  selectedItem,
  onSelect,
  onBack,
  reactions,
  reactionPreviewLimit,
  reactionsLoading,
  onWatchReaction,
  onDismiss
}) => (
  <div style={{
    position: 'fixed',
    inset: 0,
    zIndex: 120,
    background: 'rgba(0,0,0,0.78)',
    backdropFilter: 'blur(18px)',
    display: 'flex',
    alignItems: isMobile ? 'stretch' : 'center',
    justifyContent: 'center',
    padding: isMobile ? '0' : '28px'
  }}>
    <div style={{
      width: '100%',
      maxWidth: isMobile ? '100%' : '1160px',
      height: isMobile ? '100%' : 'min(90vh, 820px)',
      background: '#09090d',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: isMobile ? '0' : '28px',
      overflow: 'hidden',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : (selectedItem ? '1.45fr 0.55fr' : '1.18fr 0.82fr')
    }}>
      <div style={{
        padding: isMobile ? '18px 18px 18px' : (selectedItem ? '26px 30px 24px' : '34px 34px 28px'),
        borderRight: isMobile ? 'none' : '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflowY: isMobile ? 'auto' : undefined
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', marginBottom: selectedItem ? '12px' : '22px' }}>
          <div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '7px 10px',
              borderRadius: '999px',
              background: 'rgba(249,115,22,0.14)',
              border: '1px solid rgba(249,115,22,0.28)',
              color: '#FDBA74',
              fontSize: '11px',
              fontWeight: '800',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: selectedItem ? '10px' : '14px'
            }}>
              <Sparkles size={12} />
              Welcome to ReactorHub
            </div>
            <h1 style={{ color: colors.white, fontSize: isMobile ? '28px' : (selectedItem ? '30px' : '36px'), lineHeight: '1.04', margin: selectedItem ? '0 0 8px 0' : '0 0 12px 0', fontWeight: '800', maxWidth: '700px' }}>
              {selectedItem ? selectedItem.title : 'Pick a title. See who reacted to it.'}
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.68)', fontSize: isMobile ? '14px' : (selectedItem ? '14px' : '15px'), lineHeight: selectedItem ? '1.45' : '1.6', margin: 0, maxWidth: selectedItem ? '760px' : '680px' }}>
              {selectedItem
                ? 'These reactions load instantly so you can get a feel for the community, then hop back and try another favorite.'
                : 'Real reactions from the community, instantly. Tap any poster to try it.'}
            </p>
          </div>
        </div>

        {!selectedItem && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(3, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
          gap: isMobile ? '10px' : '14px',
          marginBottom: '24px'
        }}>
          {items.map((item) => {
            const active = selectedItem && String(selectedItem.id || selectedItem.dbId) === String(item.id || item.dbId);
            return (
              <button
                key={item.id || item.dbId || item.title}
                onClick={() => onSelect(item)}
                className="onboarding-poster-card"
                style={{
                  border: active ? '1px solid rgba(139,92,246,0.75)' : '1px solid rgba(255,255,255,0.08)',
                  background: active ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.03)',
                  borderRadius: '18px',
                  overflow: 'hidden',
                  padding: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease, background 0.28s ease',
                  boxShadow: '0 10px 24px rgba(0,0,0,0.18)'
                }}
              >
                <div className="onboarding-poster-media" style={{ aspectRatio: '0.72', background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                  {item.posterUrl ? (
                    <img
                      src={item.posterUrl}
                      alt={item.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.45s ease, filter 0.3s ease' }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.36)' }}>
                      <Film size={26} />
                    </div>
                  )}
                </div>
                <div style={{ padding: isMobile ? '10px 10px 12px' : '12px 12px 14px' }}>
                  <div style={{ color: colors.white, fontSize: isMobile ? '12px' : '13px', fontWeight: '700', lineHeight: '1.35', minHeight: isMobile ? '34px' : '38px' }}>
                    {item.title}
                  </div>
                  <div style={{ color: active ? '#C4B5FD' : 'rgba(255,255,255,0.44)', fontSize: '11px', marginTop: '6px' }}>
                    Tap to see reactions
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        )}

        {!selectedItem && (
          <button
            onClick={onDismiss}
            style={{
              width: '100%',
              padding: '14px 18px',
              borderRadius: '14px',
              border: 'none',
              background: 'linear-gradient(135deg, rgba(139,92,246,1), rgba(249,115,22,0.95))',
              color: colors.white,
              fontSize: '14px',
              fontWeight: '800',
              cursor: 'pointer',
              boxShadow: '0 12px 24px rgba(139,92,246,0.22)',
              marginTop: '4px'
            }}
          >
            Enter ReactorHub
          </button>
        )}

        {selectedItem && (
        <div style={{
          flex: 1,
          minHeight: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px', paddingRight: isMobile ? 0 : '8px' }}>
            <button
              onClick={onBack}
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '14px',
                border: '1px solid rgba(255,255,255,0.1)',
                background: 'rgba(255,255,255,0.03)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(255,255,255,0.82)',
                cursor: 'pointer',
                flexShrink: 0
              }}
            >
              <ChevronLeft size={18} />
            </button>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '14px',
              background: 'rgba(139,92,246,0.16)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#C4B5FD',
              flexShrink: 0
            }}>
              <PlayCircle size={20} />
            </div>
            <div>
              <div style={{ color: colors.white, fontSize: '17px', fontWeight: '700' }}>
                {selectedItem?.title || 'Reaction Preview'}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginTop: '2px' }}>
                A few real reactions from the community
              </div>
            </div>
            {!isMobile && (
              <button
                onClick={onDismiss}
                style={{
                  marginLeft: 'auto',
                  padding: '10px 18px',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, rgba(139,92,246,1), rgba(249,115,22,0.95))',
                  color: colors.white,
                  fontSize: '13px',
                  fontWeight: '800',
                  cursor: 'pointer',
                  flexShrink: 0,
                  boxShadow: '0 8px 20px rgba(139,92,246,0.25)'
                }}
              >
                Enter ReactorHub
              </button>
            )}
          </div>

          {reactionsLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: '12px' }}>
              {[0, 1, 2].map((index) => (
                <div key={index} style={{ borderRadius: '16px', background: 'rgba(255,255,255,0.05)', height: isMobile ? '132px' : '188px', animation: 'pulse 1.5s ease-in-out infinite' }} />
              ))}
            </div>
          ) : reactions && reactions.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
              gap: isMobile ? '16px' : '20px',
              overflowY: 'auto',
              paddingRight: isMobile ? '0' : '8px',
              alignContent: 'start',
              gridAutoRows: 'min-content'
            }}>
              {reactions.slice(0, reactionPreviewLimit).map((reaction, index) => (
                <button
                  key={reaction.id || `${reaction.videoId || reaction.title}-${index}`}
                  onClick={() => onWatchReaction(reaction)}
                  style={{
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    background: 'rgba(255,255,255,0.035)',
                    padding: 0,
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 18px 44px rgba(0,0,0,0.24)'
                  }}
                >
                  <div style={{ position: 'relative', aspectRatio: '16 / 9', background: '#050505' }}>
                    {reaction.thumbnail ? (
                      <img
                        src={reaction.thumbnail}
                        alt={reaction.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                      />
                    ) : null}
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.12) 45%, rgba(0,0,0,0.45) 100%)'
                    }} />
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: isMobile ? '72px' : '88px',
                      height: isMobile ? '72px' : '88px',
                      borderRadius: '999px',
                      background: 'rgba(255,255,255,0.86)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 18px 40px rgba(0,0,0,0.32)',
                      backdropFilter: 'blur(8px)'
                    }}>
                      <Play size={isMobile ? 28 : 34} color="#111111" fill="#111111" style={{ marginLeft: '4px' }} />
                    </div>
                  </div>
                  <div style={{
                    padding: isMobile ? '14px 14px 16px' : '16px 16px 18px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    minHeight: isMobile ? '138px' : '158px'
                  }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      alignSelf: 'flex-start',
                      padding: '6px 10px',
                      borderRadius: '999px',
                      background: 'rgba(59,130,246,0.22)',
                      border: '1px solid rgba(96,165,250,0.18)',
                      color: '#BFDBFE',
                      fontSize: '12px',
                      fontWeight: '600',
                      maxWidth: '100%'
                    }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {selectedItem?.title || 'Featured reaction'}
                      </span>
                    </div>

                    <div style={{
                      color: colors.white,
                      fontSize: isMobile ? '18px' : '19px',
                      fontWeight: '700',
                      lineHeight: '1.35',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      minHeight: isMobile ? '72px' : '78px'
                    }}>
                      {reaction.title}
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      marginTop: 'auto'
                    }}>
                      <div style={{
                        color: 'rgba(255,255,255,0.74)',
                        fontSize: isMobile ? '14px' : '15px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0
                      }}>
                        {reaction.channelName || 'Reaction channel'}
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: 'rgba(255,255,255,0.82)',
                        fontSize: '13px',
                        fontWeight: '700',
                        flexShrink: 0
                      }}>
                        <Eye size={14} />
                        {reaction.views ? formatCompactNumber(reaction.views) : ''}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div style={{
              flex: 1,
              borderRadius: '20px',
              border: '1px dashed rgba(255,255,255,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '14px',
              textAlign: 'center',
              padding: '24px'
            }}>
              We're still filling this title with reactions. Pick another favorite to keep exploring.
            </div>
          )}
        </div>
        )}

        {isMobile && selectedItem && (
          <button
            onClick={onDismiss}
            style={{
              margin: '16px 0 4px',
              width: '100%',
              padding: '16px',
              borderRadius: '14px',
              border: 'none',
              background: 'linear-gradient(135deg, rgba(139,92,246,1), rgba(249,115,22,0.95))',
              color: colors.white,
              fontSize: '15px',
              fontWeight: '800',
              cursor: 'pointer',
              boxShadow: '0 12px 24px rgba(139,92,246,0.22)',
              flexShrink: 0
            }}
          >
            Enter ReactorHub
          </button>
        )}
      </div>

      <div style={{
        padding: isMobile ? '18px' : (selectedItem ? '30px 24px 24px' : '34px 30px 28px'),
        display: isMobile ? 'none' : 'flex',
        flexDirection: 'column',
        minHeight: 0,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))'
      }}>
        <div style={{ marginBottom: '18px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '7px 10px',
            borderRadius: '999px',
            background: 'rgba(234,179,8,0.12)',
            border: '1px solid rgba(234,179,8,0.22)',
            color: '#FCD34D',
            fontSize: '11px',
            fontWeight: '800',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: '14px'
          }}>
            <Flame size={12} />
            Peak Moments
          </div>
          <h2 style={{ color: colors.white, fontSize: isMobile ? '24px' : '30px', lineHeight: '1.08', margin: '0 0 10px 0', fontWeight: '800' }}>
            The best moments, already found for you
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.64)', fontSize: '14px', lineHeight: '1.65', margin: 0 }}>
            Fans mark the scenes that hit hardest — epic, emotional, shocking, or funny. Skip straight to what everyone's talking about.
          </p>
        </div>

        <div style={{
          borderRadius: '22px',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(255,255,255,0.02)',
          marginBottom: '18px'
        }}>
          <img
            src="/peak_moments_shoot.png"
            alt="Peak Moments preview"
            style={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: selectedItem && !isMobile ? '240px' : 'none' }}
          />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
          {['Epic', 'Viral', 'Plot Twist', 'Emotional', 'Funny', 'Shocking', 'Wholesome', 'Dramatic'].map((chip) => (
            <span
              key={chip}
              style={{
                padding: '8px 12px',
                borderRadius: '999px',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: 'rgba(255,255,255,0.82)',
                fontSize: '12px',
                fontWeight: '600'
              }}
            >
              {chip}
            </span>
          ))}
        </div>

      </div>
    </div>
  </div>
);

const toChannelSlug = (name) => {
  if (!name) return '';
  return name.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

// SEO slug for media pages (/title/{slug}). MUST stay in sync with
// server/src/utils/mediaSlug.js toMediaSlug — both sides generate these URLs.
const toMediaSlug = (content) => {
  const title = content?.title || '';
  if (!title) return '';
  const year = content?.releaseYear || content?.year ||
    content?.release_date?.slice(0, 4) || content?.first_air_date?.slice(0, 4) || '';
  const base = year ? `${title} ${year}` : title;
  return base.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

const ReactorChannelPage = ({ slug, allChannels, onBack }) => {
  const channel = allChannels.find(ch => toChannelSlug(ch.name || ch.title) === slug);
  const [videos, setVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const channelId = channel?.id || channel?.channelId;

  useEffect(() => {
    if (!channel) return;
    document.title = `${channel.name || channel.title} | ReactorHub`;
    setLoadingVideos(true);
    axios.get(`${API_URL}/reactions/channel/${channelId}/recent?limit=9`)
      .then(res => setVideos(res.data.videos || []))
      .catch(() => setVideos([]))
      .finally(() => setLoadingVideos(false));
  }, [channelId]);

  const subs = channel?.statistics?.subscriberCount || channel?.subscriberCount || 0;
  const subsLabel = subs >= 1000000
    ? `${(subs / 1000000).toFixed(1)}M subscribers`
    : subs >= 1000
    ? `${Math.round(subs / 1000)}K subscribers`
    : subs > 0 ? `${subs} subscribers` : 'Reactor Channel';
  const youtubeUrl = channel?.youtubeUrl || (channelId ? `https://www.youtube.com/channel/${channelId}` : null);

  if (!channel) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 20px', color: colors.gray[400] }}>
        <Loader style={{ animation: 'spin 1s linear infinite', marginBottom: '16px', color: colors.purple[500] }} size={32} />
        <p>Loading channel...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 4px' }}>
      <button
        onClick={onBack}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: colors.gray[400], cursor: 'pointer', fontSize: '14px', marginBottom: '24px', padding: 0 }}
        onMouseEnter={e => e.currentTarget.style.color = colors.white}
        onMouseLeave={e => e.currentTarget.style.color = colors.gray[400]}
      >
        <ChevronLeft size={18} /> Back to Explore
      </button>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px', marginBottom: '36px', flexWrap: 'wrap' }}>
        <img
          src={channel.thumbnail || channel.avatar || ''}
          alt={channel.name || channel.title}
          style={{ width: '96px', height: '96px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #8B5CF6', flexShrink: 0 }}
          onError={e => { e.target.onerror = null; e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="96" height="96"%3E%3Crect fill="%23404040" width="96" height="96"/%3E%3C/svg%3E'; }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ color: colors.white, fontSize: '28px', fontWeight: '800', margin: '0 0 6px' }}>
            {channel.name || channel.title}
          </h1>
          <p style={{ color: colors.gray[400], fontSize: '14px', margin: '0 0 12px' }}>{subsLabel}</p>
          {channel.description && (
            <p style={{ color: colors.gray[300], fontSize: '13px', margin: '0 0 12px', lineHeight: '1.5' }}>
              {channel.description.length > 200 ? channel.description.slice(0, 200) + '…' : channel.description}
            </p>
          )}
          {youtubeUrl && (
            <a
              href={youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => { if (channelId) creatorAPI.trackClick(channelId, 'reactor_page', null, null, 'channel_redirect', null); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '20px', background: '#FF0000', color: colors.white, fontWeight: '600', fontSize: '13px', textDecoration: 'none' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <Youtube size={14} /> Visit YouTube Channel
            </a>
          )}
        </div>
      </div>

      <h2 style={{ color: colors.white, fontSize: '20px', fontWeight: '700', marginBottom: '20px' }}>
        Latest Reaction Videos
      </h2>
      {loadingVideos ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Loader style={{ animation: 'spin 1s linear infinite', color: colors.purple[500] }} size={28} />
        </div>
      ) : videos.length === 0 ? (
        <p style={{ color: colors.gray[500], fontSize: '14px' }}>No reaction videos ingested yet for this channel.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px' }}>
          {videos.map(video => {
            const thumb = video.thumbnailUrl?.medium || video.thumbnailUrl?.default || `https://img.youtube.com/vi/${video.videoId}/mqdefault.jpg`;
            const timeAgo = (() => {
              if (!video.publishedAt) return null;
              const diff = (Date.now() - new Date(video.publishedAt)) / 1000;
              if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
              if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
              if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
              if (diff < 31536000) return `${Math.floor(diff / 2592000)}mo ago`;
              return `${Math.floor(diff / 31536000)}y ago`;
            })();
            return (
              <a
                key={video.videoId}
                href={`https://www.youtube.com/watch?v=${video.videoId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', display: 'block', borderRadius: '10px', overflow: 'hidden', background: colors.gray[800] }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                <img src={thumb} alt={video.title} style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }}
                  onError={e => { e.target.onerror = null; e.target.style.background = colors.gray[700]; }}
                />
                <div style={{ padding: '10px 12px' }}>
                  <p style={{ color: colors.white, fontSize: '13px', fontWeight: '600', margin: '0 0 4px', lineHeight: '1.4', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {video.title}
                  </p>
                  {timeAgo && (
                    <p style={{ color: colors.gray[500], fontSize: '11px', margin: 0 }}>{timeAgo}</p>
                  )}
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Fan-deck hero for Reactions of the Week (Explore tab):
// three overlapping tilted thumbnail cards — #1 centered with gold border,
// #2 tilted right, #3 tilted left — snapping straight and lifting on hover.
const ReactionsOfWeekHero = ({ videos, loading, isMusic, isMobile, onPlay }) => {
  const glowA = isMusic ? 'rgba(234,179,8,0.35)' : 'rgba(255,95,168,0.35)';
  const glowB = isMusic ? 'rgba(249,115,22,0.28)' : 'rgba(155,92,255,0.28)';
  const pillLabel = isMusic ? '\u{1F3B5} MUSIC SPOTLIGHT' : '\u{1F525} THIS WEEK\'S HEAT';
  const heading = isMusic ? 'Music Reactor Spotlight' : 'Reactions of the Week';
  const subheading = isMusic
    ? 'Live performances, albums, and music-first reactions'
    : "The three clips everyone's losing it over";

  const list = (videos || []).slice(0, 3);
  const badges = [
    { label: '#1 PICK', bg: '#ffe45a', color: '#100c14', tilt: -3 },
    { label: '#2', bg: '#9b5cff', color: '#ffffff', tilt: 4 },
    { label: '#3', bg: '#ff5fa8', color: '#100c14', tilt: -4 }
  ];

  const fmtK = (n) => {
    const v = Number(n) || 0;
    return v >= 1000 ? `${Math.round(v / 1000)}K` : String(v);
  };

  const cardInner = (video, rankIdx, big) => (
    <div style={{
      width: '100%',
      height: '100%',
      borderRadius: big ? '24px' : '22px',
      border: big ? '3px solid #ffe45a' : '3px solid #1f1628',
      boxShadow: big
        ? `0 26px 60px ${glowA}, 0 0 0 6px rgba(255,228,90,0.08)`
        : '0 20px 50px rgba(0,0,0,0.55)',
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: '#2c2038',
      backgroundImage: video?.thumbnail
        ? `url(${video.thumbnail})`
        : 'repeating-linear-gradient(135deg, #3a2a4a 0 10px, #2c2038 10px 20px)',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, transparent 35%, rgba(0,0,0,0.88) 100%)'
      }} />
      <div style={{
        position: 'absolute', top: big ? 16 : 14, left: big ? 16 : 14,
        background: badges[rankIdx].bg, color: badges[rankIdx].color,
        fontFamily: 'ui-monospace, monospace', fontWeight: 800,
        fontSize: big ? 13 : 12, padding: big ? '5px 11px' : '4px 9px',
        borderRadius: '100px', transform: `rotate(${badges[rankIdx].tilt}deg)`,
        boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
      }}>{badges[rankIdx].label}</div>
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        width: big ? 72 : 58, height: big ? 72 : 58, borderRadius: '50%',
        background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '2px solid rgba(255,255,255,0.6)'
      }}>
        <Play size={big ? 30 : 24} fill="#fff" style={{ color: '#fff', marginLeft: 4 }} />
      </div>
      <div style={{ position: 'absolute', left: big ? 20 : 16, right: big ? 20 : 16, bottom: big ? 18 : 14 }}>
        <div style={{
          fontWeight: 700, fontSize: big ? 18 : 14, color: '#fff',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
        }}>{video?.title || 'Coming Soon'}</div>
        <div style={{
          fontFamily: 'ui-monospace, monospace', fontWeight: 600,
          fontSize: big ? 12 : 11, color: 'rgba(255,255,255,0.65)', marginTop: 4,
          display: 'flex', gap: 12, alignItems: 'center'
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Eye size={12} /> {fmtK(video?.views)}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <ThumbsUp size={12} /> {fmtK(video?.likes)}
          </span>
        </div>
      </div>
    </div>
  );

  // Desktop fan positions: index into `list` (slot order), rest/hover transforms
  const fanSlots = [
    {
      listIdx: 2, rankIdx: 2, big: false,
      wrap: { position: 'absolute', left: '2%', top: 14, width: 340, height: 190, zIndex: 1 },
      rest: 'rotate(-6deg)', hover: 'rotate(-1deg) translateY(-10px) scale(1.05)'
    },
    {
      listIdx: 0, rankIdx: 0, big: true,
      wrap: { position: 'relative', width: 384, height: 214, zIndex: 3 },
      rest: 'none', hover: 'translateY(-10px) scale(1.05)'
    },
    {
      listIdx: 1, rankIdx: 1, big: false,
      wrap: { position: 'absolute', right: '2%', top: 14, width: 340, height: 190, zIndex: 1 },
      rest: 'rotate(6deg)', hover: 'rotate(1deg) translateY(-10px) scale(1.05)'
    }
  ];

  const hasVideos = list.length > 0;

  return (
    <div style={{
      marginBottom: 32,
      borderRadius: 20,
      position: 'relative',
      overflow: 'hidden',
      padding: isMobile ? '16px 16px 14px' : '16px 28px 18px',
      background: 'radial-gradient(120% 160% at 15% 0%, #241a33 0%, #170f21 45%, #100b17 100%)',
      animation: 'slideDown 0.6s ease-out'
    }}>
      <div style={{
        position: 'absolute', top: -60, right: -60, width: 280, height: 280, borderRadius: '50%',
        background: `radial-gradient(circle, ${glowA}, transparent 70%)`, filter: 'blur(10px)', pointerEvents: 'none'
      }} />
      <div style={{
        position: 'absolute', bottom: -80, left: '20%', width: 320, height: 320, borderRadius: '50%',
        background: `radial-gradient(circle, ${glowB}, transparent 70%)`, filter: 'blur(10px)', pointerEvents: 'none'
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
          <div style={{
            display: 'inline-block', fontFamily: 'ui-monospace, monospace', fontWeight: 800,
            fontSize: 10, letterSpacing: '0.08em', color: '#ffe45a',
            background: 'rgba(255,228,90,0.12)', border: '1px solid rgba(255,228,90,0.4)',
            padding: '4px 10px', borderRadius: 100, transform: 'rotate(-3deg)', alignSelf: 'center'
          }}>{pillLabel}</div>
          <h2 style={{
            fontSize: isMobile ? 20 : 24, fontWeight: 900, color: '#fff',
            margin: 0, letterSpacing: '-0.01em', lineHeight: 1.05
          }}>{heading}</h2>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: 0 }}>
            {subheading}
          </p>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
          <Loader size={32} style={{ color: '#fff', animation: 'spin 1s linear infinite' }} />
        </div>
      ) : isMobile ? (
        <div style={{
          display: 'flex', gap: 12, overflowX: 'auto', WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none', msOverflowStyle: 'none', padding: '4px 4px 8px', position: 'relative', zIndex: 1
        }}>
          {(hasVideos ? list : [null, null, null]).map((video, i) => (
            <div
              key={video?.videoId || video?._id || i}
              onClick={() => video && onPlay(video)}
              style={{ width: 250, height: 140, flexShrink: 0, cursor: video ? 'pointer' : 'default', opacity: video ? 1 : 0.55 }}
            >
              {cardInner(video, i, i === 0)}
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          position: 'relative', zIndex: 1, display: 'flex',
          justifyContent: 'center', alignItems: 'center', height: 216, marginTop: 14
        }}>
          {fanSlots.map((slot) => {
            const video = hasVideos ? list[slot.listIdx] : null;
            if (hasVideos && !video) return null;
            return (
              <div
                key={slot.rankIdx}
                onClick={() => video && onPlay(video)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = slot.hover;
                  e.currentTarget.style.zIndex = '6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = slot.rest;
                  e.currentTarget.style.zIndex = String(slot.wrap.zIndex);
                }}
                style={{
                  ...slot.wrap,
                  transform: slot.rest,
                  transition: 'transform 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.3)',
                  willChange: 'transform',
                  cursor: video ? 'pointer' : 'default',
                  opacity: video ? 1 : 0.55
                }}
              >
                {cardInner(video, slot.rankIdx, slot.big)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Main ReactorHub component
const ReactorHub = () => {
  const getInitialRouteState = () => {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';

    if (path === '/forum') return { activeTab: 'forum', threadSlug: null, channelSlug: null, mediaSlug: null };

    const forumMatch = path.match(/^\/forum\/([^/]+)$/);
    if (forumMatch) return { activeTab: 'forum', threadSlug: decodeURIComponent(forumMatch[1]), channelSlug: null, mediaSlug: null };

    const reactorMatch = path.match(/^\/reactor\/([^/]+)$/);
    if (reactorMatch) return { activeTab: 'explore', threadSlug: null, channelSlug: decodeURIComponent(reactorMatch[1]), mediaSlug: null };

    const titleMatch = path.match(/^\/title\/([^/]+)$/);
    if (titleMatch) return { activeTab: 'home', threadSlug: null, channelSlug: null, mediaSlug: decodeURIComponent(titleMatch[1]) };

    return { activeTab: 'home', threadSlug: null, channelSlug: null, mediaSlug: null };
  };

  const initialRouteState = getInitialRouteState();
  const [activeTab, setActiveTab] = useState(initialRouteState.activeTab);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // The feed is a full-screen portal, so it opens straight over whatever tab
  // you're on — no navigating through Peak Moments first.
  const [feedMoments, setFeedMoments] = useState(null); // null = closed
  const [feedLoading, setFeedLoading] = useState(false);

  const openMomentsFeed = async () => {
    if (feedLoading) return;
    setFeedLoading(true);
    try {
      const res = await peakMomentsAPI.getPeakMoments({ page: 1, limit: 50 });
      const list = (res.data || []).filter(m => m.videoId);
      setFeedMoments(list.length ? list : []);
    } catch (err) {
      console.error('Failed to load peak moments feed:', err);
      setFeedMoments([]);
    } finally {
      setFeedLoading(false);
    }
  };
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [currentThreadSlug, setCurrentThreadSlug] = useState(initialRouteState.threadSlug);
  const [currentChannelSlug, setCurrentChannelSlug] = useState(initialRouteState.channelSlug || null);
  // Media slug from /title/{slug} URLs awaiting resolution to a content object
  const [pendingMediaSlug, setPendingMediaSlug] = useState(initialRouteState.mediaSlug || null);
  // Declared here (not with the other content state below) because the routing
  // effects that follow need to reference the media modal state
  const [selectedContent, setSelectedContent] = useState(null);
  const [contentPosition, setContentPosition] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const routeState = getInitialRouteState();
      setActiveTab(routeState.activeTab);
      setCurrentThreadSlug(routeState.threadSlug);
      setCurrentChannelSlug(routeState.channelSlug || null);
      if (routeState.mediaSlug) {
        setPendingMediaSlug(routeState.mediaSlug);
      } else {
        // Navigated away from a /title/ URL (e.g. back button) — close the modal
        setPendingMediaSlug(null);
        setSelectedContent(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Resolve a /title/{slug} URL (direct visit, refresh, or forward navigation)
  // into a content object and open the media overlay with it
  useEffect(() => {
    if (!pendingMediaSlug) return;
    if (selectedContent && toMediaSlug(selectedContent) === pendingMediaSlug) {
      setPendingMediaSlug(null);
      return;
    }
    let cancelled = false;
    contentAPI.getContentBySlug(pendingMediaSlug)
      .then((response) => {
        if (cancelled) return;
        if (response?.content) {
          setContentPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
          setSelectedContent(response.content);
        }
      })
      .catch(() => {
        // Unknown slug — fall back to the homepage URL
        if (!cancelled) window.history.replaceState({}, '', '/');
      })
      .finally(() => {
        if (!cancelled) setPendingMediaSlug(null);
      });
    return () => { cancelled = true; };
  }, [pendingMediaSlug, selectedContent]);

  useEffect(() => {
    // While a /title/{slug} URL is still resolving, leave the URL alone
    if (pendingMediaSlug) return;

    const mediaSlug = selectedContent ? toMediaSlug(selectedContent) : null;
    const nextPath = mediaSlug
      ? `/title/${mediaSlug}`
      : currentThreadSlug
        ? `/forum/${encodeURIComponent(currentThreadSlug)}`
        : currentChannelSlug
          ? `/reactor/${currentChannelSlug}`
          : activeTab === 'forum'
            ? '/forum'
            : '/';

    if (window.location.pathname !== nextPath) {
      window.history.replaceState({}, '', nextPath);
    }
  }, [activeTab, currentThreadSlug, currentChannelSlug, selectedContent, pendingMediaSlug]);

  useEffect(() => {
    // Media overlay gets its own title (e.g. "The Dark Knight Rises (2012) Reactions | ReactorHub")
    if (selectedContent) {
      const year = selectedContent.releaseYear || selectedContent.year || '';
      document.title = `${selectedContent.title}${year ? ` (${year})` : ''} Reactions | ReactorHub`;
      return;
    }
    // ThreadDetail and ReactorChannelPage manage their own titles
    if (currentThreadSlug || currentChannelSlug) return;
    const titles = {
      home:           'ReactorHub | Find Reaction Videos for Movies, Shows, Anime & Music',
      explore:        'Explore Reactors | ReactorHub',
      'peak-moments': 'Peak Moments | ReactorHub',
      votes:          'Vote on Reactions | ReactorHub',
      music:          'Music Reactions | ReactorHub',
      forum:          'Community Forum | ReactorHub',
      favorites:      'My Space | ReactorHub',
      creator:        'Creator Profile | ReactorHub',
    };
    document.title = titles[activeTab] || titles.home;
  }, [activeTab, currentThreadSlug, currentChannelSlug, selectedContent]);

  useEffect(() => {
    const key = 'rh_session';
    let sessionId = sessionStorage.getItem(key);
    if (!sessionId) {
      sessionId = Math.random().toString(36).slice(2);
      sessionStorage.setItem(key, sessionId);
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const _u = getUserFromToken();
      fetch(`${API_BASE}/track-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ referrer: document.referrer || '', sessionId, userEmail: _u?.email || null })
      }).catch(() => {});
    }
  }, []);
  const [featuredOfWeek, setFeaturedOfWeek] = useState({});
  const [exploreMediaRankings, setExploreMediaRankings] = useState({
    featuredMovie: null,
    featuredTvShow: null,
    featuredMusic: null,
    mostWanted: []
  });
  const [homeFeedMode, setHomeFeedMode] = useState('all');
  const [exploreFeedMode, setExploreFeedMode] = useState('all');
  const [exploreFilter, setExploreFilter] = useState('solo');
  const [channelModal, setChannelModal] = useState(null); // { channel, catColor, videos, loading }
  const [exploreEmbedVideoId, setExploreEmbedVideoId] = useState(null);
  const [peakMomentEmbed, setPeakMomentEmbed] = useState(null);
  const [showCreateThreadModal, setShowCreateThreadModal] = useState(false);
  const [linkedContent, setLinkedContent] = useState(null);
  const [favoritedChannels, setFavoritedChannels] = useState(new Set());

  // Auth state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showFirstVisitOnboarding, setShowFirstVisitOnboarding] = useState(false);
  const [onboardingItems, setOnboardingItems] = useState([]);
  const [onboardingLoading, setOnboardingLoading] = useState(true);
  const [selectedOnboardingItem, setSelectedOnboardingItem] = useState(null);
  const [onboardingReactions, setOnboardingReactions] = useState([]);
  const [onboardingReactionsLoading, setOnboardingReactionsLoading] = useState(false);
  const [onboardingEmbed, setOnboardingEmbed] = useState(null);
  const onboardingReactionPreviewLimit = isMobile ? 4 : 8;

  const { data: trendingMovies, loading: moviesLoading } = useContent('movie');
  const { data: trendingShows, loading: showsLoading } = useContent('tv');
  const { data: trendingAnime, loading: animeLoading } = useContent('anime');
  const { data: trendingMusic, loading: musicLoading } = useContent('music');
  const { data: channels, loading: channelsLoading } = useFeaturedChannels();
  const { data: reactionOfWeekVideos, loading: reactionOfWeekLoading } = useReactionOfWeek('general');
  const { data: musicReactionOfWeekVideos, loading: musicReactionOfWeekLoading } = useReactionOfWeek('music');

  // Check if user is authenticated on mount
  useEffect(() => {
    if (isAuthenticated()) {
      const user = getUserFromToken();
      setCurrentUser(user);
    }
  }, []);

  useEffect(() => {
    const dismissed = window.localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (dismissed) {
      setOnboardingLoading(false);
      return;
    }

    let cancelled = false;
    const loadOnboardingItems = async () => {
      try {
        const moviePool = Array.isArray(trendingMovies) ? trendingMovies : [];
        const matches = await Promise.all(
          ONBOARDING_TITLES.map(async ({ title, searchAliases = [], fallback = null }) => {
            try {
              for (const query of searchAliases) {
                const response = await contentAPI.searchContent(query, 'movie');
                const searchedMatch = pickBestOnboardingMatch(response.results || [], title);
                if (searchedMatch) return searchedMatch;
              }
            } catch (error) {
              console.error(`Error searching onboarding title "${title}":`, error);
            }

            return pickBestOnboardingMatch(moviePool, title) || fallback;
          })
        );

        const resolved = matches.filter(Boolean);
        if (!cancelled) {
          setOnboardingItems(resolved);
          setSelectedOnboardingItem(null);
          setShowFirstVisitOnboarding(resolved.length > 0);
        }
      } catch (error) {
        console.error('Error loading onboarding content:', error);
        if (!cancelled) {
          setOnboardingItems([]);
          setShowFirstVisitOnboarding(false);
        }
      } finally {
        if (!cancelled) {
          setOnboardingLoading(false);
        }
      }
    };

    loadOnboardingItems();
    return () => {
      cancelled = true;
    };
  }, [trendingMovies]);

  useEffect(() => {
    if (!showFirstVisitOnboarding || !selectedOnboardingItem) return;

    let cancelled = false;
    const loadOnboardingReactions = async () => {
      try {
        setOnboardingReactionsLoading(true);
        const reactionSearchTitle = buildReactionSearchTitle(selectedOnboardingItem);
        const response = await reactionAPI.getReactions(reactionSearchTitle, { page: 1, pageSize: onboardingReactionPreviewLimit });
        if (!cancelled) {
          setOnboardingReactions(response.reactions || []);
        }
      } catch (error) {
        console.error('Error loading onboarding reactions:', error);
        if (!cancelled) {
          setOnboardingReactions([]);
        }
      } finally {
        if (!cancelled) {
          setOnboardingReactionsLoading(false);
        }
      }
    };

    loadOnboardingReactions();
    return () => {
      cancelled = true;
    };
  }, [showFirstVisitOnboarding, selectedOnboardingItem, onboardingReactionPreviewLimit]);

  const handleAuthSuccess = (user) => {
    setCurrentUser(user);
    setShowAuthModal(false);
    // Refresh any user-specific data if needed
  };

  const handleLogout = () => {
    removeAuthToken();
    setCurrentUser(null);
  };

  const dismissFirstVisitOnboarding = () => {
    window.localStorage.setItem(ONBOARDING_STORAGE_KEY, 'dismissed');
    setShowFirstVisitOnboarding(false);
    setOnboardingEmbed(null);
  };

  const handleOnboardingReactionWatch = (reaction) => {
    if (reaction?.videoId) {
      if (reaction.channelId) {
        creatorAPI.trackClick(reaction.channelId, 'onboarding_preview', null, null, 'video_embed_play', null);
      }
      setOnboardingEmbed({
        videoId: reaction.videoId,
        title: reaction.title
      });
      return;
    }

    trackAndOpen(reaction?.channelId, reaction?.youtubeUrl, 'onboarding_preview');
  };

  // Load sidebar rankings from database
  useEffect(() => {
    const loadRankings = async () => {
      try {
        console.log('ðŸ“Š Loading sidebar rankings from database...');
        const response = await featuredAPI.getSidebarRankings();
        console.log('ðŸ“Š Public rankings data:', response);

        if (response && response.success && response.rankings) {
          console.log('âœ… Sidebar rankings loaded:', response.rankings);
          // Map the database structure to frontend structure
          const mapRanking = (ranking) => {
            if (!ranking || !ranking.channelId) return null;
            return {
              id: ranking.channelId,
              name: ranking.name,
              thumbnail: ranking.thumbnail
            };
          };

          const mapMediaRanking = (ranking) => {
            if (!ranking || !(ranking.contentId || ranking.dbId) || !ranking.title) return null;
            return {
              id: ranking.contentId || ranking.dbId,
              dbId: ranking.dbId || ranking.contentId,
              title: ranking.title,
              type: ranking.type,
              posterUrl: ranking.posterUrl || '',
              releaseYear: ranking.releaseYear || null,
              year: ranking.releaseYear || null,
              seasonCount: ranking.seasonCount || null,
              seasons: ranking.seasonCount || null,
              artist: ranking.artist || '',
              genre: ranking.genre || '',
              favoriteCount: ranking.favoriteCount || 0,
              voteCount: ranking.voteCount || 0
            };
          };

          setFeaturedOfWeek({
            channeloftheweek: mapRanking(response.rankings.channelOfWeek),
            risingstar: mapRanking(response.rankings.risingStar),
            mosttalkedabout: mapRanking(response.rankings.mostTalkedAbout),
            staffpick: mapRanking(response.rankings.staffPick)
          });
          setExploreMediaRankings({
            featuredMovie: mapMediaRanking(response.rankings.featuredMovie),
            featuredTvShow: mapMediaRanking(response.rankings.featuredTvShow),
            featuredMusic: mapMediaRanking(response.rankings.featuredMusic),
            mostWanted: (response.rankings.mostWanted || []).map(mapMediaRanking).filter(Boolean)
          });
        }
      } catch (error) {
        console.error('âŒ Error loading sidebar rankings:', error);
      }
    };
    loadRankings();
  }, []);

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} days ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / (86400 * 7))} weeks ago`;
    if (diff < 86400 * 365) return `${Math.floor(diff / (86400 * 30))} months ago`;
    return `${Math.floor(diff / (86400 * 365))} years ago`;
  };

  const openChannelModal = async (channel, catColor) => {
    const channelId = channel.id || channel.channelId;
    setChannelModal({ channel, catColor, videos: [], loading: true });
    try {
      const res = await axios.get(`${API_URL}/reactions/channel/${channelId}/recent?limit=4`);
      setChannelModal(prev => prev ? { ...prev, videos: res.data.videos || [], loading: false } : null);
    } catch {
      setChannelModal(prev => prev ? { ...prev, videos: [], loading: false } : null);
    }
  };

  const sidebarFeaturedMovie = exploreMediaRankings.featuredMovie;
  const sidebarFeaturedTvShow = exploreMediaRankings.featuredTvShow;
  const sidebarFeaturedMusic = exploreMediaRankings.featuredMusic;
  const sidebarMostWanted = exploreMediaRankings.mostWanted || [];
  const musicChannels = (channels || []).filter((channel) =>
    channel?.primaryCategory === 'music-performance' ||
    channel?.categories?.includes('music-performance')
  );
  const musicByArtist = {};
  if (trendingMusic) {
    trendingMusic.forEach((music) => {
      const key = music.artist || 'Various Artists';
      if (!musicByArtist[key]) musicByArtist[key] = [];
      musicByArtist[key].push(music);
    });
  }
  const musicArtistEntries = Object.entries(musicByArtist)
    .filter(([, items]) => items.length > 0)
    .sort((a, b) => b[1].length - a[1].length);
  const spotlightReactionVideos = exploreFeedMode === 'music' ? musicReactionOfWeekVideos : reactionOfWeekVideos;
  const spotlightReactionLoading = exploreFeedMode === 'music' ? musicReactionOfWeekLoading : reactionOfWeekLoading;

  // Load user's favorited channels
  useEffect(() => {
    const loadFavoritedChannels = async () => {
      try {
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        if (!token) return;

        const response = await axios.get(`${API_URL}/myspace/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success && response.data.data.favorites) {
          const channelIds = response.data.data.favorites
            .filter(fav => fav.type === 'channel')
            .map(fav => fav.channelId);

          console.log('âœ… Loaded favorited channels:', channelIds);
          setFavoritedChannels(new Set(channelIds));
        }
      } catch (error) {
        console.error('âŒ Error loading favorited channels:', error);
      }
    };
    loadFavoritedChannels();
  }, []);

  // Check for new reactions when user logs in or returns to platform
  useEffect(() => {
    const checkNewReactions = async () => {
      try {
        // Check if user is authenticated (has token)
        const token = localStorage.getItem('token') || sessionStorage.getItem('token') ||
                     localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

        if (!token) {
          console.log('ðŸ”’ User not authenticated, skipping reaction check');
          return;
        }

        console.log('ðŸ”” Checking for new reactions...');
        const response = await axios.post(`${API_URL}/myspace/check-new-reactions`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data.success && response.data.newReactionsCount > 0) {
          console.log(`âœ… Found ${response.data.newReactionsCount} new reaction(s)!`);
        } else {
          console.log('â„¹ï¸ No new reactions found');
        }
      } catch (error) {
        console.error('âŒ Error checking for new reactions:', error);
      }
    };

    checkNewReactions();
  }, []);

  const handleContentSelect = (content, rect) => {
    if (rect) {
        setContentPosition({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
    } else {
        setContentPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
    }
    // Give the media overlay a real URL (SEO + shareable); pushState so the
    // browser back button closes the overlay and returns to the previous view
    const slug = toMediaSlug(content);
    if (slug && window.location.pathname !== `/title/${slug}`) {
      window.history.pushState({}, '', `/title/${slug}`);
    }
    setSelectedContent(content);
  };

  // Handle start thread for reactor videos
  const handleStartThread = (contentInfo) => {
    console.log('Start thread for:', contentInfo);
    setLinkedContent(contentInfo);
    setShowCreateThreadModal(true);
  };

  // Handle view thread
  const handleViewThread = (slug, threadId) => {
    console.log('View thread:', slug, threadId);
    window.history.pushState({}, '', `/forum/${encodeURIComponent(slug)}`);
    setCurrentThreadSlug(slug);
    setActiveTab('forum');
  };

  const navigateToTab = (tab) => {
    setCurrentChannelSlug(null);
    setCurrentThreadSlug(null);
    setActiveTab(tab);
  };

  const handleChannelPageOpen = (channel) => {
    const slug = toChannelSlug(channel.name || channel.title);
    if (!slug) { openChannelModal(channel, '#8B5CF6'); return; }
    window.history.pushState({}, '', `/reactor/${slug}`);
    setCurrentChannelSlug(slug);
    setActiveTab('explore');
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: colors.black,
      color: colors.white,
      overflowX: 'hidden'
    }}>
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.6; }
          100% { opacity: 1; }
        }

        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes tickerSlide {
          from {
            opacity: 0;
            transform: translateX(-50px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }

        @keyframes shine {
          0% {
            left: -100%;
          }
          100% {
            left: 200%;
          }
        }

        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }

        * {
          box-sizing: border-box;
          scrollbar-width: thin;
          scrollbar-color: ${colors.gray[700]} ${colors.gray[900]};
        }

        *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        *::-webkit-scrollbar-track {
          background: ${colors.gray[900]};
        }

        *::-webkit-scrollbar-thumb {
          background-color: ${colors.gray[700]};
          border-radius: 10px;
          border: 2px solid ${colors.gray[900]};
        }
        *::-webkit-scrollbar-thumb:hover {
          background-color: ${colors.gray[600]};
        }

        .horizontal-scroll::-webkit-scrollbar {
          display: none;
        }
        .horizontal-scroll {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .onboarding-poster-card:hover {
          transform: translateY(-8px) scale(1.025);
          border-color: rgba(249,115,22,0.45) !important;
          box-shadow: 0 18px 38px rgba(0,0,0,0.34), 0 0 0 1px rgba(249,115,22,0.18);
        }

        .onboarding-poster-card:hover .onboarding-poster-media img {
          transform: scale(1.06);
          filter: saturate(1.06) brightness(1.04);
        }

        @media (max-width: 768px) {
          .onboarding-poster-card:hover {
            transform: translateY(-4px) scale(1.01);
          }

          /* Force full width on all major content wrappers */
          main > div { max-width: 100% !important; }

          /* Card grids: 2 columns on mobile */
          .grid-auto { grid-template-columns: repeat(2, 1fr) !important; }

          /* Horizontal scroll cards: smaller on mobile */
          .movie-card { min-width: 120px !important; max-width: 120px !important; width: 120px !important; height: 180px !important; }
          .channel-card { min-width: 140px !important; width: 140px !important; }

          /* Peak moments grid */
          .moments-grid { grid-template-columns: repeat(2, 1fr) !important; }

          /* Forum thread rows */
          .thread-title { font-size: 13px !important; }

          /* Vote poll cards */
          .poll-option { font-size: 13px !important; }

          /* Hide right sidebar panels that don't fit */
          .desktop-only { display: none !important; }

          /* Ensure text doesn't overflow */
          h1 { font-size: 1.4rem !important; }
          h2 { font-size: 1.1rem !important; }

          /* Filter tab rows: allow wrapping */
          .filter-tabs { flex-wrap: wrap !important; gap: 6px !important; }

          /* Home featured banner */
          .featured-banner { height: 220px !important; }
        }
      `}</style>

      {!isMobile && (
        <Sidebar
          activeTab={activeTab}
          setActiveTab={navigateToTab}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          currentUser={currentUser}
          setShowAuthModal={setShowAuthModal}
          handleLogout={handleLogout}
          onPlayFeed={openMomentsFeed}
        />
      )}

      {!onboardingLoading && showFirstVisitOnboarding && onboardingItems.length > 0 && (
        <FirstVisitOnboarding
          isMobile={isMobile}
          items={onboardingItems}
          selectedItem={selectedOnboardingItem}
          onSelect={setSelectedOnboardingItem}
          onBack={() => setSelectedOnboardingItem(null)}
          reactions={onboardingReactions}
          reactionPreviewLimit={onboardingReactionPreviewLimit}
          reactionsLoading={onboardingReactionsLoading}
          onWatchReaction={handleOnboardingReactionWatch}
          onDismiss={dismissFirstVisitOnboarding}
        />
      )}

      {feedMoments && feedMoments.length > 0 && (
        <PeakMomentsFeed
          moments={feedMoments}
          startIndex={0}
          onClose={() => setFeedMoments(null)}
          onLike={async (moment) => {
            try { await peakMomentsAPI.likePeakMoment(moment._id || moment.id); } catch { /* ignore */ }
          }}
          onOpenFull={(moment) => {
            setFeedMoments(null);
            if (moment.videoId) {
              setPeakMomentEmbed({
                videoId: moment.videoId,
                title: moment.title || moment.videoTitle,
                startSeconds: moment.timestampSeconds,
              });
            }
          }}
        />
      )}

      {onboardingEmbed && (
        <VideoEmbedModal
          videoId={onboardingEmbed.videoId}
          title={onboardingEmbed.title}
          onClose={() => setOnboardingEmbed(null)}
        />
      )}

      <main style={{
        transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        marginLeft: isMobile ? 0 : (sidebarCollapsed ? '76px' : '256px'),
        padding: isMobile ? '12px' : '32px',
        paddingBottom: isMobile ? '80px' : '32px',
        paddingTop: isMobile ? '60px' : '32px',
        minWidth: 0,
        overflowX: 'hidden'
      }}>

        {/* Mobile Top Bar */}
        {isMobile && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0,
            height: '52px',
            backgroundColor: '#0a0a0f',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 12px',
            zIndex: 50,
          }}>
            <img src="/assets/brand/logo-nobg.png" alt="" style={{ height: '30px', width: '30px', flexShrink: 0 }} />
            <img
              src="/assets/brand/reactorhub_brand_nameart.png"
              alt="ReactorHub"
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '140px',
                height: 'auto',
                pointerEvents: 'none'
              }}
            />
            <span style={{
              position: 'absolute',
              left: 'calc(50% + 72px)',
              top: '9px',
              fontSize: '8px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
              color: '#FFFFFF',
              padding: '2px 5px',
              borderRadius: '4px',
              letterSpacing: '0.4px',
              textTransform: 'uppercase',
              pointerEvents: 'none',
              lineHeight: '1'
            }}>BETA</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => navigateToTab('music')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px' }}>
                <Music style={{ width: '20px', height: '20px', color: activeTab === 'music' ? '#8B5CF6' : 'rgba(255,255,255,0.4)' }} />
              </button>
              {currentUser ? (
                <button onClick={() => navigateToTab('favorites')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px' }}>
                  <User style={{ width: '20px', height: '20px', color: activeTab === 'favorites' ? '#8B5CF6' : 'rgba(255,255,255,0.4)' }} />
                </button>
              ) : (
                <button onClick={() => setShowAuthModal(true)} style={{
                  minHeight: '32px',
                  padding: '7px 10px',
                  borderRadius: '999px',
                  border: '1px solid rgba(139, 92, 246, 0.45)',
                  background: 'linear-gradient(135deg, rgba(139,92,246,0.95), rgba(249,115,22,0.9))',
                  color: colors.white,
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  boxShadow: '0 8px 18px rgba(139,92,246,0.25)'
                }}>
                  Sign up
                </button>
              )}
            </div>
          </div>
        )}

        {/* HOME TAB */}
        {activeTab === 'home' && (
          <div style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}>
              <HomeSignupPrompt
                isMobile={isMobile}
                currentUser={currentUser}
                onSignup={() => setShowAuthModal(true)}
              />

              <FeedModeTabs
                value={homeFeedMode}
                onChange={setHomeFeedMode}
                compact={isMobile}
                options={[
                  { id: 'all', label: 'Home Feed' },
                  { id: 'music', label: 'Music Feed', disabled: true }
                ]}
              />
              {homeFeedMode === 'music' && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '-6px',
                  marginBottom: '8px',
                  paddingLeft: '4px',
                  flexWrap: 'wrap'
                }}>
                  <Music size={11} color={colors.yellow[400]} />
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)' }}>
                    Reactions organized around artists, albums & music drops
                  </span>
                  <span style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    color: colors.yellow[400],
                    background: 'rgba(234,179,8,0.1)',
                    border: '1px solid rgba(234,179,8,0.25)',
                    borderRadius: '5px',
                    padding: '1px 6px'
                  }}>
                    In Development
                  </span>
                </div>
              )}

            <div style={{ display: 'flex', gap: '20px', overflow: 'hidden' }}>
            {/* Main Content */}
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              {homeFeedMode === 'all' ? (
                <>
                  <FeaturedReactionsCarousel isMobile={isMobile} onMediaSelect={handleContentSelect} />

                  <SearchComponent
                    onSearch={handleContentSelect}
                    loading={false}
                  />

                  {/* Featured Reactors + first featured Peak Moments side by side */}
                  <div style={{
                    display: 'flex',
                    gap: '24px',
                    alignItems: 'flex-start',
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                    marginBottom: '10px'
                  }}>
                    <div style={{ flexShrink: 0, minWidth: 0, maxWidth: '100%' }}>
                      <TopReactorsCarousel onChannelClick={(ch) => openChannelModal({ id: ch.channelId, channelId: ch.channelId, name: ch.channelName, title: ch.channelName, thumbnail: ch.thumbnail }, '#8B5CF6')} />
                    </div>
                    {!isMobile && (
                      <div style={{
                        width: '1px',
                        alignSelf: 'stretch',
                        background: 'linear-gradient(180deg, transparent, rgba(255,255,255,0.12) 20%, rgba(255,255,255,0.12) 80%, transparent)',
                        flexShrink: 0,
                        marginBottom: '16px'
                      }} />
                    )}
                    {!isMobile && (
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <FeaturedPeakMomentsStrip
                          onPlay={(moment) => {
                            if (moment.videoId) {
                              if (moment.channelId) {
                                creatorAPI.trackClick(moment.channelId, 'peak_moments', null, null, 'peak_moment_embed_play', null);
                              }
                              setPeakMomentEmbed({
                                videoId: moment.videoId,
                                title: moment.title || moment.videoTitle,
                                startSeconds: moment.timestampSeconds
                              });
                            } else if (moment.videoUrl) {
                              trackAndOpen(moment.channelId, moment.videoUrl, 'peak_moments');
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <SupportBanner isMobile={isMobile} />

                  <HorizontalScrollSection title="Movies" loading={moviesLoading}>
                    {trendingMovies?.map((movie, index) => (
                      <MoviePosterCard
                        key={movie.id}
                        show={movie}
                        index={index}
                        onSelect={handleContentSelect}
                        type="movie"
                      />
                    ))}
                  </HorizontalScrollSection>

                </>
              ) : (
                <>
                  <FeaturedReactionsCarousel isMobile={isMobile} audience="music" onMediaSelect={handleContentSelect} />


                  <SearchComponent
                    onSearch={handleContentSelect}
                    loading={false}
                  />

                  {musicChannels.length > 0 && (
                    <HorizontalScrollSection title="Top Music Reactors" loading={channelsLoading}>
                      {musicChannels.map((channel) => (
                        <ChannelCard key={channel.id || channel.youtubeChannelId || channel.channelName} channel={channel} onChannelClick={(ch) => openChannelModal(ch, '#8B5CF6')} />
                      ))}
                    </HorizontalScrollSection>
                  )}

                </>
              )}
            </div>

            {/* Right Sidebar - Trending Media */}
            <div style={{
              width: '340px',
              flexShrink: 0,
              alignSelf: 'stretch',
              background: '#120d1a',
              borderRadius: '20px',
              border: '1px solid rgba(255,255,255,0.06)',
              padding: '16px',
              boxSizing: 'border-box',
              display: isMobile ? 'none' : 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              {/* Sidebar Header */}
              <div style={{
                background: homeFeedMode === 'music'
                  ? 'linear-gradient(90deg, rgba(234,179,8,0.25) 0%, rgba(249,115,22,0.15) 100%)'
                  : 'linear-gradient(90deg, rgba(155,92,255,0.25) 0%, rgba(255,95,168,0.15) 100%)',
                padding: '9px 14px',
                borderRadius: '12px',
                flexShrink: 0,
                fontSize: '13px',
                fontWeight: '800',
                color: colors.white,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {homeFeedMode === 'music'
                  ? <><Music size={15} color={colors.yellow[400]} />Music This Week</>
                  : <><Flame size={15} color="#ff8a3d" />Trending This Week</>
                }
              </div>

              {/* Top Movie â€” hidden in music mode */}
              {homeFeedMode !== 'music' && (
                sidebarFeaturedMovie ? (
                  <TrendingSideRow
                    tag="🎬 MOVIE #1"
                    tagColor="#ff5fa8"
                    tilt="-6deg"
                    badgeNum={1}
                    badgeBg="#ffe45a"
                    badgeColor="#100c14"
                    title={sidebarFeaturedMovie.title}
                    meta={`${sidebarFeaturedMovie.releaseYear || sidebarFeaturedMovie.year || ''} · ♥ ${sidebarFeaturedMovie.favoriteCount || 0} · 👍 ${sidebarFeaturedMovie.voteCount || 0}`}
                    posterUrl={sidebarFeaturedMovie.posterUrl}
                    onClick={() => handleContentSelect(sidebarFeaturedMovie)}
                  />
                ) : (
                  <div style={{ color: colors.gray[500], textAlign: 'center', padding: '10px', fontSize: '12px' }}>
                    Movie #1 not set in admin yet
                  </div>
                )
              )}

              {/* Top TV Show â€” hidden in music mode */}
              {homeFeedMode !== 'music' && (
                sidebarFeaturedTvShow ? (
                  <TrendingSideRow
                    tag="📺 TV SHOW #1"
                    tagColor="#9b5cff"
                    tilt="5deg"
                    badgeNum={1}
                    badgeBg="#9b5cff"
                    badgeColor="#fff"
                    title={sidebarFeaturedTvShow.title}
                    meta={`${sidebarFeaturedTvShow.seasonCount || sidebarFeaturedTvShow.seasons || 1} Seasons · ♥ ${sidebarFeaturedTvShow.favoriteCount || 0} · 👍 ${sidebarFeaturedTvShow.voteCount || 0}`}
                    posterUrl={sidebarFeaturedTvShow.posterUrl}
                    onClick={() => handleContentSelect(sidebarFeaturedTvShow)}
                  />
                ) : (
                  <div style={{ color: colors.gray[500], textAlign: 'center', padding: '10px', fontSize: '12px' }}>
                    TV Show #1 not set in admin yet
                  </div>
                )
              )}

              {/* Most Wanted Reactions â€” hidden in music mode */}
              {homeFeedMode !== 'music' && sidebarMostWanted.length > 0 && (
                <TrendingSideRow
                  tag="🔥 RISING"
                  tagColor="#ff8a3d"
                  tilt="-4deg"
                  badgeNum={2}
                  badgeBg="#ff5fa8"
                  badgeColor="#100c14"
                  title={sidebarMostWanted[0].title}
                  meta={`${sidebarMostWanted[0].year || sidebarMostWanted[0].releaseYear || ''}`}
                  posterUrl={sidebarMostWanted[0].posterUrl}
                  onClick={() => handleContentSelect(sidebarMostWanted[0])}
                />
              )}

              {homeFeedMode !== 'music' && (
                <>
                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', margin: '2px 0', flexShrink: 0 }} />
                  <div style={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: '12px',
                    fontWeight: '800',
                    color: '#ff8a3d',
                    letterSpacing: '0.04em',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Flame size={12} color="#ff8a3d" /> MOST WANTED
                  </div>
                  {sidebarMostWanted.length > 1 ? (
                    sidebarMostWanted.slice(1, 3).map((movie, index) => (
                      <div
                        key={movie.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          cursor: 'pointer',
                          padding: '4px',
                          borderRadius: '10px',
                          flexShrink: 0,
                          transition: 'background 0.15s'
                        }}
                        onClick={() => handleContentSelect(movie)}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ position: 'relative', flexShrink: 0 }}>
                          <div style={{
                            width: '78px',
                            height: '106px',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            background: 'linear-gradient(135deg, #241a33, #1a1322)'
                          }}>
                            {movie.posterUrl && (
                              <img
                                src={movie.posterUrl}
                                alt={movie.title}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            )}
                          </div>
                          <div style={{
                            position: 'absolute',
                            top: '-6px',
                            left: '-6px',
                            width: '19px',
                            height: '19px',
                            borderRadius: '50%',
                            background: '#ff8a3d',
                            color: '#100c14',
                            fontFamily: "'Space Mono', monospace",
                            fontSize: '11px',
                            fontWeight: '800',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {index + 3}
                          </div>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '700',
                            color: colors.white,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {movie.title}
                          </div>
                          <div style={{ fontSize: '11px', fontWeight: '500', color: 'rgba(255,255,255,0.4)' }}>
                            {movie.year || movie.releaseYear || ''}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ color: colors.gray[500], fontSize: '11px' }}>Not set in admin yet</div>
                  )}
                </>
              )}

              {/* Music Featured — only in music feed mode */}
              {homeFeedMode === 'music' && (
                <div style={{ padding: '4px 0' }}>
                  {sidebarFeaturedMusic ? (
                    <div
                      style={{ display: 'flex', gap: '12px', padding: '6px', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s', margin: '-6px', alignItems: 'center' }}
                      onClick={() => handleContentSelect(sidebarFeaturedMusic)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(234,179,8,0.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <img
                        src={sidebarFeaturedMusic.posterUrl || ''}
                        alt={sidebarFeaturedMusic.title}
                        style={{ width: 'auto', height: '130px', maxHeight: '95%', borderRadius: '8px', objectFit: 'cover', flexShrink: 0, boxShadow: '0 4px 12px rgba(0,0,0,0.4)' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Music size={12} color={colors.yellow[400]} />
                          <span style={{ color: colors.yellow[400], fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Music #1</span>
                        </div>
                        <h4 style={{ color: colors.white, fontSize: '13px', fontWeight: '600', marginBottom: '0', lineHeight: '1.3', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {sidebarFeaturedMusic.title}
                        </h4>
                        {sidebarFeaturedMusic.artist && (
                          <p style={{ color: colors.gray[500], fontSize: '11px', margin: 0 }}>{sidebarFeaturedMusic.artist}</p>
                        )}
                        <div style={{ display: 'flex', gap: '10px', fontSize: '11px', color: colors.gray[400] }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <Heart size={10} color={colors.purple[400]} />
                            <span>{sidebarFeaturedMusic.favoriteCount || 0}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <ThumbsUp size={10} color={colors.green[400]} />
                            <span>{sidebarFeaturedMusic.voteCount || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: '12px', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginBottom: '4px' }}>
                        <Music size={12} color={colors.yellow[400]} />
                        <span style={{ color: colors.yellow[400], fontSize: '10px', fontWeight: '600', textTransform: 'uppercase' }}>Music</span>
                      </div>
                      <span style={{ color: colors.gray[500], fontSize: '11px' }}>Not set in admin yet</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            </div>

            {/* Full-width rows below the two-column area */}
            {homeFeedMode === 'all' ? (
              <>
                <HorizontalScrollSection title="TV Shows" loading={showsLoading}>
                  {trendingShows?.map((show, index) => (
                    <MoviePosterCard
                      key={show.id}
                      show={show}
                      index={index}
                      onSelect={handleContentSelect}
                      type="series"
                    />
                  ))}
                </HorizontalScrollSection>

                <HorizontalScrollSection title="Anime" loading={animeLoading}>
                  {trendingAnime?.map((anime, index) => (
                    <MoviePosterCard
                      key={anime.id}
                      show={anime}
                      index={index}
                      onSelect={handleContentSelect}
                      type="anime"
                    />
                  ))}
                </HorizontalScrollSection>
              </>
            ) : (
              <>
                <HorizontalScrollSection title="Trending Music" loading={musicLoading}>
                  {trendingMusic?.map((music, index) => (
                    <MoviePosterCard
                      key={music.id}
                      show={music}
                      index={index}
                      onSelect={handleContentSelect}
                      type="music"
                    />
                  ))}
                </HorizontalScrollSection>

                {musicArtistEntries.slice(0, 4).map(([artist, items]) => (
                  <HorizontalScrollSection key={artist} title={artist} loading={false}>
                    {items.map((music, index) => (
                      <MoviePosterCard
                        key={music.id}
                        show={music}
                        index={index}
                        onSelect={handleContentSelect}
                        type="music"
                      />
                    ))}
                  </HorizontalScrollSection>
                ))}
              </>
            )}

            <div style={{
              marginTop: isMobile ? '18px' : '24px',
              padding: isMobile ? '6px 2px 0' : '8px 0 0',
              color: 'rgba(255,255,255,0.38)',
              fontSize: isMobile ? '11px' : '12px',
              lineHeight: '1.5',
              textAlign: 'center',
            }}>
              <div>
                ReactorHub uses public YouTube API data. Media posters and metadata are provided through the Streaming Availability API.
              </div>
              <div style={{ marginTop: '8px', color: 'rgba(255,255,255,0.28)' }}>
                © {new Date().getFullYear()} ReactorHub. All rights reserved.
              </div>
            </div>
          </div>
        )}

        {/* PEAK MOMENTS TAB */}
        {activeTab === 'peak-moments' && (
          <div style={{
            marginLeft: isMobile ? '-12px' : '-32px',
            marginRight: isMobile ? '-12px' : '-32px',
            marginTop: isMobile ? '-12px' : '-32px',
            minHeight: 'calc(100vh - 32px)'
          }}>
            <CommunityPeakMoments />
          </div>
        )}

        {/* EXPLORE TAB */}
        {activeTab === 'explore' && (
          currentChannelSlug ? (
            <ReactorChannelPage
              slug={currentChannelSlug}
              allChannels={[...(channels || []), ...(musicChannels || [])]}
              onBack={() => {
                window.history.pushState({}, '', '/');
                setCurrentChannelSlug(null);
              }}
            />
          ) : (
          <div style={{ display: 'flex', gap: '24px', overflow: 'hidden' }}>
            {/* Main Content */}
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <FeedModeTabs
                value={exploreFeedMode}
                onChange={setExploreFeedMode}
                compact={isMobile}
                options={[
                  { id: 'all', label: 'Explore All' },
                  { id: 'music', label: 'Explore Music', disabled: true }
                ]}
              />

              {/* Spotlight Banner: Reactions of the Week (fan-deck hero) */}
              <ReactionsOfWeekHero
                videos={spotlightReactionVideos}
                loading={spotlightReactionLoading}
                isMusic={exploreFeedMode === 'music'}
                isMobile={isMobile}
                onPlay={(video) => {
                  if (video.videoId) {
                    if (video.channelId) {
                      creatorAPI.trackClick(video.channelId, 'peak_moments', null, null, 'peak_moment_embed_play', null);
                    }
                    setPeakMomentEmbed({
                      videoId: video.videoId,
                      title: video.title || video.videoTitle,
                      startSeconds: video.timestampSeconds
                    });
                  } else {
                    trackAndOpen(video.channelId, video.videoUrl, 'peak_moments');
                  }
                }}
              />

              {/* Filter Tabs */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{
                  display: 'flex',
                  gap: isMobile ? '2px' : '16px',
                  borderBottom: `2px solid ${colors.gray[800]}`,
                  marginBottom: '24px',
                  overflowX: 'auto',
                  scrollbarWidth: 'none'
                }}>
                  {[
                    { label: 'Solo', reactorType: 'solo', emoji: '🎯' },
                    { label: 'Together', reactorType: 'duo', emoji: '💑' },
                    { label: 'Group', reactorType: 'group', emoji: '👥' },
                    { label: 'Family', reactorType: 'family', emoji: '👨‍👩‍👧‍👦' },
                    { label: 'Anime', reactorType: 'anime', emoji: '🎌' }
                  ].map(tab => (
                    <button
                      key={tab.reactorType}
                      onClick={() => setExploreFilter(tab.reactorType)}
                      style={{
                        padding: isMobile ? '10px 12px' : '12px 24px',
                        flexShrink: 0,
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderBottom: `3px solid ${exploreFilter === tab.reactorType ? colors.purple[500] : 'transparent'}`,
                        color: exploreFilter === tab.reactorType ? colors.white : colors.gray[400],
                        fontSize: isMobile ? '13px' : '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        marginBottom: '-2px',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        if (exploreFilter !== tab.reactorType) {
                          e.currentTarget.style.color = colors.gray[300];
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (exploreFilter !== tab.reactorType) {
                          e.currentTarget.style.color = colors.gray[400];
                        }
                      }}
                    >
                      {tab.emoji} {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reactor Type Categories */}
              {(() => {
                // Define all categories with their metadata
                const allCategories = [
                  { name: 'Solo Vibes', icon: User, color: '#8B5CF6', emoji: '🎯', reactorType: 'solo' },
                  { name: 'Reacting Together', icon: Users, color: '#EC4899', emoji: '💑', reactorType: 'duo' },
                  { name: 'Group Energy', icon: Users, color: '#F97316', emoji: '👥', reactorType: 'group' },
                  { name: 'Family Feels', icon: Heart, color: '#10B981', emoji: '👨‍👩‍👧‍👦', reactorType: 'family' },
                  { name: 'Anime Reactors', icon: Tv, color: '#A78BFA', emoji: '🎌', reactorType: 'anime', filterByCategory: true }
                ];

                // Reorder categories based on selected filter - move selected to top
                const orderedCategories = [...allCategories];
                const selectedIndex = orderedCategories.findIndex(cat => cat.reactorType === exploreFilter);
                if (selectedIndex > 0) {
                  const [selectedCategory] = orderedCategories.splice(selectedIndex, 1);
                  orderedCategories.unshift(selectedCategory);
                }

                return orderedCategories.map((catInfo, catIndex) => {
                  const Icon = catInfo.icon;
                  const category = catInfo.name;

                // Filter channels by reactorType
                const sourceChannels = exploreFeedMode === 'music' ? musicChannels : (channels || []);
                const categoryChannels = sourceChannels.filter(ch =>
                  catInfo.filterByCategory
                    ? (ch.primaryCategory === catInfo.reactorType || ch.categories?.includes(catInfo.reactorType))
                    : ch.reactorType === catInfo.reactorType
                );

                console.log(`ðŸ” Explore Tab - ${category}:`, {
                  totalChannels: sourceChannels.length || 0,
                  reactorType: catInfo.reactorType,
                  filteredChannels: categoryChannels.length,
                  allReactorTypes: sourceChannels.map(ch => ch.reactorType),
                  sampleChannelFull: sourceChannels[0]
                });

                if (categoryChannels.length === 0) return null;

                const scrollId = `scroll-${catInfo.reactorType}`;
                const scroll = (direction) => {
                  const element = document.getElementById(scrollId);
                  if (element) {
                    const scrollAmount = direction === 'left' ? -800 : 800;
                    element.scrollBy({ left: scrollAmount, behavior: 'smooth' });
                  }
                };

                return (
                  <div
                    key={catInfo.reactorType}
                    id={`carousel-${catInfo.reactorType}`}
                    style={{
                      marginBottom: '17px',
                      position: 'relative',
                      animation: catIndex === 0 ? 'slideDown 0.5s ease-out' : 'none',
                      transition: 'all 0.5s ease-out'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                      <h3 style={{
                        color: colors.white,
                        fontSize: '20px',
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <Icon size={24} style={{ color: catInfo.color }} />
                        {catInfo.emoji} {category}
                      </h3>

                      {categoryChannels.length > 8 && (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => scroll('left')}
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              border: `2px solid ${catInfo.color}`,
                              backgroundColor: colors.gray[800],
                              color: catInfo.color,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.3s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = catInfo.color;
                              e.currentTarget.style.color = colors.white;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = colors.gray[800];
                              e.currentTarget.style.color = catInfo.color;
                            }}
                          >
                            <ChevronLeft size={20} />
                          </button>
                          <button
                            onClick={() => scroll('right')}
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              border: `2px solid ${catInfo.color}`,
                              backgroundColor: colors.gray[800],
                              color: catInfo.color,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.3s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = catInfo.color;
                              e.currentTarget.style.color = colors.white;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = colors.gray[800];
                              e.currentTarget.style.color = catInfo.color;
                            }}
                          >
                            <ChevronRight size={20} />
                          </button>
                        </div>
                      )}
                    </div>

                    {channelsLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Loader style={{ width: '32px', height: '32px', color: colors.purple[500], animation: 'spin 1s linear infinite' }} />
                      </div>
                    ) : (
                      <div style={{ overflow: 'visible', position: 'relative' }}>
                        <div
                          id={scrollId}
                          style={{
                            display: 'flex',
                            gap: '24px',
                            overflowX: 'scroll',
                            paddingBottom: '16px',
                            paddingTop: '12px',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            WebkitOverflowScrolling: 'touch'
                          }}
                          className="hide-scrollbar"
                        >
                        {categoryChannels.map((channel, index) => {
                          const channelUrl = channel.youtubeUrl || channel.channelUrl || (channel.id ? `https://youtube.com/channel/${channel.id}` : null);
                          const channelId = channel.id || channel.channelId;
                          const isFavorited = favoritedChannels.has(channelId);

                          const handleFavoriteClick = async (e) => {
                            e.stopPropagation();

                            try {
                              const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
                              if (!token) {
                                alert('Please log in to favorite channels');
                                return;
                              }

                              if (isFavorited) {
                                // Remove from favorites
                                await axios.delete(`${API_URL}/myspace/favorites/${channelId}`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                setFavoritedChannels(prev => {
                                  const newSet = new Set(prev);
                                  newSet.delete(channelId);
                                  return newSet;
                                });
                                console.log('âœ… Removed from favorites:', channel.name);
                              } else {
                                // Add to favorites
                                await axios.post(`${API_URL}/myspace/favorites/channel`, {
                                  channelId: channelId,
                                  channelName: channel.name || channel.title,
                                  thumbnail: channel.thumbnail || channel.avatar,
                                  subscriberCount: channel.statistics?.subscriberCount || 0
                                }, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                setFavoritedChannels(prev => new Set(prev).add(channelId));
                                console.log('âœ… Added to favorites:', channel.name);
                              }
                            } catch (error) {
                              console.error('âŒ Error toggling favorite:', error);
                              if (error.response?.data?.error) {
                                alert(error.response.data.error);
                              }
                            }
                          };

                          return (
                            <div
                              key={channel.id || channel.name}
                              onClick={() => handleChannelPageOpen(channel)}
                              style={{
                                minWidth: '140px',
                                transition: 'all 0.3s',
                                cursor: 'pointer',
                                textAlign: 'center',
                                flexShrink: 0,
                                position: 'relative'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-8px)';
                                const img = e.currentTarget.querySelector('img');
                                if (img) {
                                  img.style.boxShadow = `0 12px 32px ${catInfo.color}60`;
                                  img.style.borderColor = catInfo.color;
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                const img = e.currentTarget.querySelector('img');
                                if (img) {
                                  img.style.boxShadow = '0 4px 12px rgba(0,0,0,0.4)';
                                  img.style.borderColor = '#404040';
                                }
                              }}
                            >
                        <div style={{ position: 'relative', display: 'inline-block', marginBottom: '12px' }}>
                          <img
                            src={channel.thumbnail || channel.avatar || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="120"%3E%3Crect fill="%23404040" width="120" height="120"/%3E%3C/svg%3E'}
                            alt={channel.name || channel.title}
                            style={{
                              width: '120px',
                              height: '120px',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              border: `3px solid #404040`,
                              display: 'block',
                              transition: 'all 0.3s',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="120"%3E%3Crect fill="%23404040" width="120" height="120"/%3E%3C/svg%3E';
                            }}
                          />
                          {(channel.primaryCategory === 'music-performance' || channel.primaryCategory === 'music' || channel.categories?.includes('music-performance') || channel.categories?.includes('music')) && (
                            <div style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              background: 'rgba(234,179,8,0.15)',
                              border: '1px solid rgba(234,179,8,0.5)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Music size={11} color="#eab308" />
                            </div>
                          )}
                        </div>
                        <h4 style={{
                          color: colors.white,
                          fontSize: '14px',
                          fontWeight: '600',
                          marginBottom: '6px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '140px'
                        }}>
                          {channel.name || channel.title}
                        </h4>
                        <p style={{
                          color: '#A3A3A3',
                          fontSize: '11px',
                          marginBottom: '8px'
                        }}>
                          {channel.statistics?.subscriberCount ?
                            (channel.statistics.subscriberCount >= 1000000
                              ? `${(channel.statistics.subscriberCount / 1000000).toFixed(1)}M subs`
                              : channel.statistics.subscriberCount >= 1000
                              ? `${(channel.statistics.subscriberCount / 1000).toFixed(0)}K subs`
                              : `${channel.statistics.subscriberCount} subs`)
                            : 'Reactor'}
                        </p>

                        {/* Favorite Button */}
                        <button
                          onClick={handleFavoriteClick}
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            backgroundColor: isFavorited ? colors.red[500] : colors.gray[800],
                            border: `1.5px solid ${isFavorited ? colors.red[500] : colors.gray[600]}`,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s',
                            margin: '0 auto'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1)';
                            e.currentTarget.style.backgroundColor = isFavorited ? colors.red[600] : colors.gray[700];
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.backgroundColor = isFavorited ? colors.red[500] : colors.gray[800];
                          }}
                        >
                          <Heart
                            size={14}
                            style={{
                              color: isFavorited ? colors.white : colors.gray[400],
                              fill: isFavorited ? colors.white : 'none',
                              strokeWidth: 2
                            }}
                          />
                        </button>
                      </div>
                          );
                        })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
              })()}
            </div>

            {/* OLD Featured of the Week Sidebar - DEACTIVATED */}
            {false && <div style={{ width: '320px', flexShrink: 0 }}>
              <div style={{
                position: 'sticky',
                top: '32px',
                backgroundColor: colors.gray[900],
                borderRadius: '16px',
                padding: '24px',
                border: `1px solid ${colors.gray[800]}`
              }}>
                <h3 style={{
                  color: colors.white,
                  fontSize: '20px',
                  fontWeight: '700',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Star style={{ color: colors.yellow[400] }} size={24} />
                  Featured of the Week
                </h3>

                {/* Channel of the Week */}
                <div
                  style={{
                    marginBottom: '24px',
                    padding: '16px',
                    backgroundColor: colors.gray[800],
                    borderRadius: '12px',
                    border: `2px solid ${colors.purple[500]}`,
                    animation: 'pulse 2s ease-in-out infinite',
                    cursor: featuredOfWeek.channeloftheweek ? 'pointer' : 'default'
                  }}
                  onClick={() => featuredOfWeek.channeloftheweek && openChannelModal(featuredOfWeek.channeloftheweek, '#8B5CF6')}
                >
                  <div style={{
                    fontSize: '11px',
                    color: colors.purple[400],
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    marginBottom: '8px',
                    letterSpacing: '0.5px'
                  }}>
                    ðŸ† Channel of the Week
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    {featuredOfWeek.channeloftheweek ? (
                      <>
                        {featuredOfWeek.channeloftheweek.thumbnail ? (
                          <img
                            src={featuredOfWeek.channeloftheweek.thumbnail}
                            alt={featuredOfWeek.channeloftheweek.name}
                            style={{
                              width: '80px',
                              height: '80px',
                              borderRadius: '50%',
                              margin: '12px auto',
                              border: `3px solid ${colors.purple[500]}`,
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '50%',
                          margin: '12px auto',
                          border: `3px solid ${colors.purple[500]}`,
                          backgroundColor: colors.purple[700],
                          display: featuredOfWeek.channeloftheweek.thumbnail ? 'none' : 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '32px',
                          fontWeight: '700',
                          color: colors.white
                        }}>
                          {featuredOfWeek.channeloftheweek.name?.charAt(0) || '?'}
                        </div>
                        <h4 style={{ color: colors.white, fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                          {featuredOfWeek.channeloftheweek.name}
                        </h4>
                        <p style={{ color: colors.gray[400], fontSize: '12px' }}>
                          Featured Channel
                        </p>
                      </>
                    ) : (
                      <p style={{ color: colors.gray[500], fontSize: '14px', padding: '20px' }}>
                        No channel selected
                      </p>
                    )}
                  </div>
                </div>

                {/* Rising Star */}
                <div
                  style={{
                    marginBottom: '24px',
                    padding: '12px',
                    backgroundColor: colors.gray[800],
                    borderRadius: '12px',
                    border: `1px solid ${colors.green[500]}`,
                    cursor: featuredOfWeek.risingstar ? 'pointer' : 'default'
                  }}
                  onClick={() => featuredOfWeek.risingstar && openChannelModal(featuredOfWeek.risingstar, '#22C55E')}
                >
                  <div style={{
                    fontSize: '11px',
                    color: colors.green[400],
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <TrendingUp size={14} />
                    Rising Star
                  </div>
                  {featuredOfWeek.risingstar ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {featuredOfWeek.risingstar.thumbnail ? (
                        <img
                          src={featuredOfWeek.risingstar.thumbnail}
                          alt={featuredOfWeek.risingstar.name}
                          style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            border: `2px solid ${colors.green[500]}`,
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          border: `2px solid ${colors.green[500]}`,
                          backgroundColor: colors.green[700],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          fontWeight: '700',
                          color: colors.white
                        }}>
                          {featuredOfWeek.risingstar.name?.charAt(0) || '?'}
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <h5 style={{ color: colors.white, fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>
                          {featuredOfWeek.risingstar.name}
                        </h5>
                        <p style={{ color: colors.gray[400], fontSize: '11px' }}>
                          Trending Now
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p style={{ color: colors.gray[500], fontSize: '12px' }}>
                      No channel selected
                    </p>
                  )}
                </div>

                {/* Most Talked About */}
                <div
                  style={{
                    marginBottom: '24px',
                    padding: '12px',
                    backgroundColor: colors.gray[800],
                    borderRadius: '12px',
                    border: `1px solid ${colors.orange[500]}`,
                    cursor: featuredOfWeek.mosttalkedabout ? 'pointer' : 'default'
                  }}
                  onClick={() => featuredOfWeek.mosttalkedabout && openChannelModal(featuredOfWeek.mosttalkedabout, '#F97316')}
                >
                  <div style={{
                    fontSize: '11px',
                    color: colors.orange[400],
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Flame size={14} />
                    Most Talked About
                  </div>
                  {featuredOfWeek.mosttalkedabout ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {featuredOfWeek.mosttalkedabout.thumbnail ? (
                        <img
                          src={featuredOfWeek.mosttalkedabout.thumbnail}
                          alt={featuredOfWeek.mosttalkedabout.name}
                          style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            border: `2px solid ${colors.orange[500]}`,
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          border: `2px solid ${colors.orange[500]}`,
                          backgroundColor: colors.orange[700],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          fontWeight: '700',
                          color: colors.white
                        }}>
                          {featuredOfWeek.mosttalkedabout.name?.charAt(0) || '?'}
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <h5 style={{ color: colors.white, fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>
                          {featuredOfWeek.mosttalkedabout.name}
                        </h5>
                        <p style={{ color: colors.gray[400], fontSize: '11px' }}>
                          Hot Topic
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p style={{ color: colors.gray[500], fontSize: '12px' }}>
                      No channel selected
                    </p>
                  )}
                </div>

                {/* Staff Picks */}
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: colors.gray[800],
                    borderRadius: '12px',
                    border: `1px solid ${colors.pink[500]}`,
                    cursor: featuredOfWeek.staffpick ? 'pointer' : 'default'
                  }}
                  onClick={() => featuredOfWeek.staffpick && openChannelModal(featuredOfWeek.staffpick, '#EC4899')}
                >
                  <div style={{
                    fontSize: '11px',
                    color: colors.pink[500],
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Sparkles size={14} />
                    Staff Picks
                  </div>
                  {featuredOfWeek.staffpick ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {featuredOfWeek.staffpick.thumbnail ? (
                        <img
                          src={featuredOfWeek.staffpick.thumbnail}
                          alt={featuredOfWeek.staffpick.name}
                          style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            border: `2px solid ${colors.pink[500]}`,
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          border: `2px solid ${colors.pink[500]}`,
                          backgroundColor: colors.pink[700],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          fontWeight: '700',
                          color: colors.white
                        }}>
                          {featuredOfWeek.staffpick.name?.charAt(0) || '?'}
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <h5 style={{ color: colors.white, fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>
                          {featuredOfWeek.staffpick.name}
                        </h5>
                        <p style={{ color: colors.gray[400], fontSize: '11px' }}>
                          Editor's Choice
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p style={{ color: colors.gray[500], fontSize: '12px' }}>
                      No channel selected
                    </p>
                  )}
                </div>
              </div>
            </div>}

            {/* Right Sidebar */}
            <div style={{
              width: '320px',
              flexShrink: 0,
              display: isMobile ? 'none' : 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              {/* Leaderboard - Top 4 */}
              <div style={{
                backgroundColor: colors.gray[900],
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${colors.gray[800]}`
              }}>
                <h3 style={{
                  color: colors.white,
                  fontSize: '18px',
                  fontWeight: '700',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Award size={20} style={{ color: '#FFD700' }} />
                  Top Reactors
                </h3>

                {/* Channel of the Week - #1 */}
                {featuredOfWeek.channeloftheweek ? (
                  <div style={{
                    marginBottom: '20px',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                    borderRadius: '10px',
                    position: 'relative',
                    cursor: 'pointer'
                  }}
                  onClick={() => openChannelModal(featuredOfWeek.channeloftheweek, '#FFD700')}>
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: 'rgba(0,0,0,0.3)',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '700',
                      color: '#FFFFFF'
                    }}>
                      #1
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <img
                        src={featuredOfWeek.channeloftheweek.thumbnail}
                        alt={featuredOfWeek.channeloftheweek.name}
                        style={{
                          width: '76px',
                          height: '76px',
                          borderRadius: '50%',
                          border: '3px solid #FFFFFF',
                          objectFit: 'cover'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '700',
                          color: '#000000',
                          marginBottom: '4px'
                        }}>
                          Channel of the Week
                        </div>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '800',
                          color: '#000000'
                        }}>
                          {featuredOfWeek.channeloftheweek.name}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    padding: '16px',
                    backgroundColor: colors.gray[800],
                    borderRadius: '10px',
                    marginBottom: '20px',
                    textAlign: 'center'
                  }}>
                    <p style={{ color: colors.gray[400], fontSize: '13px' }}>
                      No Channel of the Week selected
                    </p>
                  </div>
                )}

                {/* Rising Stars - #2, #3, #4 */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: colors.gray[400],
                    marginBottom: '12px'
                  }}>
                    Rising Stars
                  </div>
                  {[featuredOfWeek.risingstar, featuredOfWeek.mosttalkedabout]
                    .filter(Boolean)
                    .map((featured, index) => (
                    <div
                      key={featured.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px',
                        borderRadius: '8px',
                        backgroundColor: index === 0 ? 'rgba(236, 72, 153, 0.1)' : colors.gray[800],
                        marginBottom: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => openChannelModal(featured, '#8B5CF6')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colors.gray[700];
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = index === 0 ? 'rgba(236, 72, 153, 0.1)' : colors.gray[800];
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: colors.gray[700],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '700',
                        color: colors.white,
                        flexShrink: 0
                      }}>
                        {index + 2}
                      </div>
                      <img
                        src={featured.thumbnail}
                        alt={featured.name}
                        style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '50%',
                          border: `2px solid ${colors.gray[700]}`,
                          objectFit: 'cover'
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: colors.white,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {featured.name}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Staff Picks */}
              <div style={{
                backgroundColor: colors.gray[900],
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${colors.gray[800]}`
              }}>
                <h3 style={{
                  color: colors.white,
                  fontSize: '18px',
                  fontWeight: '700',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Sparkles size={20} style={{ color: '#EC4899' }} />
                  Staff Picks
                </h3>
                {featuredOfWeek.staffpick ? (
                  <div
                    key={featuredOfWeek.staffpick.id}
                    style={{
                      padding: '12px',
                      borderRadius: '8px',
                      backgroundColor: colors.gray[800],
                      marginBottom: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => openChannelModal(featuredOfWeek.staffpick, '#EC4899')}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.gray[700];
                      e.currentTarget.style.transform = 'translateX(4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = colors.gray[800];
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                      <img
                        src={featuredOfWeek.staffpick.thumbnail}
                        alt={featuredOfWeek.staffpick.name}
                        style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          border: `2px solid ${colors.gray[700]}`,
                          objectFit: 'cover'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: colors.white,
                          marginBottom: '4px'
                        }}>
                          {featuredOfWeek.staffpick.name}
                        </div>
                      </div>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: colors.gray[300],
                      lineHeight: '1.4'
                    }}>
                      {channels?.find(ch => ch.id === featuredOfWeek.staffpick.id)?.description?.slice(0, 80) || 'Amazing reaction content with great energy and authenticity.'}...
                    </div>
                  </div>
                ) : (
                  <div style={{
                    padding: '16px',
                    backgroundColor: colors.gray[800],
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <p style={{ color: colors.gray[400], fontSize: '13px' }}>
                      No Staff Pick selected
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          )
        )}

        {activeTab === 'favorites' && (
          <MySpaceContent
            onLogin={() => setShowAuthModal(true)}
            onThreadClick={(slug) => {
              setCurrentThreadSlug(slug);
              setActiveTab('forum');
            }}
            onChannelClick={(ch) => openChannelModal({
              id: ch.channelId,
              channelId: ch.channelId,
              name: ch.channelName,
              title: ch.channelName,
              thumbnail: ch.thumbnail,
              channelUrl: ch.channelUrl,
              statistics: { subscriberCount: ch.subscriberCount }
            }, '#8B5CF6')}
            onContentClick={(content) => handleContentSelect({
              id: content.contentId,
              _id: content.contentId,
              title: content.title,
              showType: content.contentType,
              posterVertical: { w480: content.poster, w360: content.poster }
            })}
          />
        )}



        {activeTab === 'music' && (() => {
          // Group music by artist
          const musicByArtist = {};
          if (trendingMusic) {
            trendingMusic.forEach(m => {
              const key = m.artist || 'Various Artists';
              if (!musicByArtist[key]) musicByArtist[key] = [];
              musicByArtist[key].push(m);
            });
          }
          const artistEntries = Object.entries(musicByArtist);

          return (
          <div>
            {/* Header Banner */}
            <div style={{
              background: `linear-gradient(135deg, ${colors.yellow[500]}, ${colors.orange[500]})`,
              borderRadius: '16px',
              padding: '32px',
              marginBottom: '32px',
              display: 'flex',
              alignItems: 'center',
              gap: '20px'
            }}>
              <Music style={{ width: '48px', height: '48px', color: colors.white }} />
              <div>
                <h2 style={{ color: colors.white, fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
                  Music Reactions
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '15px' }}>
                  Discover the best music reaction content organized by artist, band, and genre
                </p>
              </div>
            </div>

            {trendingMusic && trendingMusic.length > 0 ? (
              <>
                {/* Trending Music */}
                <HorizontalScrollSection title="Trending Music" loading={musicLoading}>
                  {trendingMusic.map((music, index) => (
                    <MoviePosterCard
                      key={music.id}
                      show={music}
                      index={index}
                      onSelect={handleContentSelect}
                      type="music"
                    />
                  ))}
                </HorizontalScrollSection>

                {/* By Artist Sections */}
                {artistEntries.filter(([, items]) => items.length > 0).map(([artist, items]) => (
                  <HorizontalScrollSection key={artist} title={artist} loading={false}>
                    {items.map((music, index) => (
                      <MoviePosterCard
                        key={music.id}
                        show={music}
                        index={index}
                        onSelect={handleContentSelect}
                        type="music"
                      />
                    ))}
                  </HorizontalScrollSection>
                ))}

                {/* All Music Grid */}
                <div style={{ marginTop: '32px' }}>
                  <h3 style={{ color: colors.white, fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
                    All Music
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: '16px'
                  }}>
                    {trendingMusic.map((music, index) => (
                      <MoviePosterCard
                        key={music.id}
                        show={music}
                        index={index}
                        onSelect={handleContentSelect}
                        type="music"
                      />
                    ))}
                  </div>
                </div>
              </>
            ) : (
              /* Empty State */
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                background: 'rgba(255,255,255,0.02)',
                borderRadius: '16px',
                border: '1px solid rgba(255,255,255,0.06)'
              }}>
                <Music style={{ width: '48px', height: '48px', color: colors.gray[500], marginBottom: '16px' }} />
                <h3 style={{ color: colors.white, fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>
                  Music content is being curated
                </h3>
                <p style={{ color: colors.gray[400], fontSize: '14px' }}>
                  Check back soon for the best music reaction content
                </p>
              </div>
            )}
          </div>
          );
        })()}

        {activeTab === 'votes' && (
          <VoteReactionsTab />
        )}

        {/* FORUM TAB */}
        {activeTab === 'forum' && (
          currentThreadSlug ? (
            <ThreadDetail
              slug={currentThreadSlug}
              onBack={() => {
                window.history.pushState({}, '', '/forum');
                setCurrentThreadSlug(null);
                setActiveTab('forum');
              }}
            />
          ) : (
            <Forum
              onThreadClick={(slug) => {
                window.history.pushState({}, '', `/forum/${encodeURIComponent(slug)}`);
                setCurrentThreadSlug(slug);
                setActiveTab('forum');
              }}
            />
          )
        )}

        {/* CREATOR STUDIO TAB */}
        {activeTab === 'creator' && currentUser && (
          <CreatorDashboard currentUser={currentUser} />
        )}


        {selectedContent && (
          <ContentDetailsOverlay
            content={selectedContent}
            position={contentPosition}
            onClose={() => setSelectedContent(null)}
            onSeasonSelect={(content, season) => {
              console.log('Selected Content for Season Change:', content.title, 'Season:', season);
            }}
            onStartThread={handleStartThread}
            onViewThread={handleViewThread}
            onFavorited={() => setSelectedContent(prev => prev ? { ...prev, favoriteCount: (prev.favoriteCount || 0) + 1 } : prev)}
          />
        )}

        {/* Channel Preview Modal */}
        {channelModal && (
          <div
            onClick={() => setChannelModal(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(0,0,0,0.75)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '16px'
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: colors.gray[900],
                border: `1px solid ${channelModal.catColor || colors.purple[500]}40`,
                borderRadius: '16px',
                padding: '24px',
                width: '100%',
                maxWidth: '480px',
                boxShadow: `0 24px 64px rgba(0,0,0,0.6)`
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
                <img
                  src={channelModal.channel.thumbnail || channelModal.channel.avatar || ''}
                  alt={channelModal.channel.name}
                  style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `2px solid ${channelModal.catColor || colors.purple[500]}` }}
                  onError={e => { e.target.onerror = null; e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="52" height="52"%3E%3Crect fill="%23404040" width="52" height="52"/%3E%3C/svg%3E'; }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: colors.white, fontWeight: '700', fontSize: '15px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{channelModal.channel.name || channelModal.channel.title}</div>
                  <div style={{ color: colors.gray[400], fontSize: '12px', marginTop: '2px' }}>
                    {channelModal.channel.statistics?.subscriberCount
                      ? (channelModal.channel.statistics.subscriberCount >= 1000000
                          ? `${(channelModal.channel.statistics.subscriberCount / 1000000).toFixed(1)}M subscribers`
                          : `${(channelModal.channel.statistics.subscriberCount / 1000).toFixed(0)}K subscribers`)
                      : 'Reactor Channel'}
                  </div>
                </div>
                <button
                  onClick={() => {
                    const channelId = channelModal.channel.id || channelModal.channel.channelId;
                    const channelUrl = channelModal.channel.youtubeUrl || channelModal.channel.channelUrl || `https://www.youtube.com/channel/${channelId}`;
                    trackAndOpen(channelId, channelUrl, 'explore_tab');
                  }}
                  style={{
                    flexShrink: 0,
                    padding: '6px 12px',
                    borderRadius: '20px',
                    background: channelModal.catColor || colors.purple[500],
                    border: 'none',
                    color: colors.white,
                    fontWeight: '600',
                    fontSize: '12px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Visit Channel
                </button>
                <button
                  onClick={() => setChannelModal(null)}
                  style={{ background: 'none', border: 'none', color: colors.gray[400], cursor: 'pointer', fontSize: '22px', lineHeight: 1, padding: '4px', flexShrink: 0 }}
                >×</button>
              </div>

              {/* Video grid */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ color: colors.gray[400], fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
                  Latest Uploads
                </div>
                {channelModal.loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                    <Loader style={{ width: '28px', height: '28px', color: channelModal.catColor || colors.purple[500], animation: 'spin 1s linear infinite' }} />
                  </div>
                ) : channelModal.videos.length === 0 ? (
                  <div style={{ textAlign: 'center', color: colors.gray[500], padding: '24px 0', fontSize: '14px' }}>No videos ingested yet</div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {channelModal.videos.map(video => {
                      const thumb = video.thumbnailUrl?.medium || video.thumbnailUrl?.default || `https://img.youtube.com/vi/${video.videoId}/mqdefault.jpg`;
                      const videoUrl = video.youtubeVideoUrl || `https://www.youtube.com/watch?v=${video.videoId}`;
                      return (
                        <div
                          key={video.videoId}
                          onClick={() => { const vid = video.videoId || videoUrl.match(/[?&]v=([^&]+)/)?.[1]; vid ? setExploreEmbedVideoId(vid) : window.open(videoUrl, '_blank'); }}
                          style={{ cursor: 'pointer', borderRadius: '8px', overflow: 'hidden', background: colors.gray[800], display: 'flex', flexDirection: 'column' }}
                          onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                        >
                          <img
                            src={thumb}
                            alt={video.title}
                            style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block', flexShrink: 0 }}
                            onError={e => { e.target.onerror = null; e.target.style.background = colors.gray[700]; }}
                          />
                          <div style={{ padding: '7px 8px 8px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            <div style={{
                              fontSize: '11px',
                              color: colors.gray[200],
                              lineHeight: '1.35',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}>
                              {video.title}
                            </div>
                            {video.publishedAt && (
                              <div style={{ fontSize: '10px', color: colors.gray[500] }}>
                                {timeAgo(video.publishedAt)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          </div>
        )}

        {exploreEmbedVideoId && <VideoEmbedModal videoId={exploreEmbedVideoId} onClose={() => setExploreEmbedVideoId(null)} />}
        {peakMomentEmbed && (
          <VideoEmbedModal
            videoId={peakMomentEmbed.videoId}
            title={peakMomentEmbed.title}
            startSeconds={peakMomentEmbed.startSeconds}
            onClose={() => setPeakMomentEmbed(null)}
          />
        )}

        {/* Create Thread Modal */}
        <CreateThreadModal
          isOpen={showCreateThreadModal}
          onClose={() => {
            setShowCreateThreadModal(false);
            setLinkedContent(null);
          }}
          onSuccess={(thread) => {
            setShowCreateThreadModal(false);
            setLinkedContent(null);
            // Navigate to the new thread
            if (thread?.slug) {
              setCurrentThreadSlug(thread.slug);
              setActiveTab('forum');
            }
          }}
          linkedContent={linkedContent}
        />

        {/* Auth Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      </main>

      {/* Mobile "Play Moments" launcher — the sidebar (and its button) doesn't
          exist on mobile, so the feed needs its own entry point here. */}
      {isMobile && (
        <>
          <style>{`
            @keyframes pfm-flow { 0% { background-position: 0% 50%; } 100% { background-position: 300% 50%; } }
            @keyframes pfm-glow {
              0%, 100% { box-shadow: 0 0 0 0 rgba(249,115,22,0.55), 0 8px 20px rgba(236,72,153,0.4); }
              50%      { box-shadow: 0 0 0 10px rgba(249,115,22,0), 0 10px 26px rgba(236,72,153,0.6); }
            }
            @keyframes pfm-eq { 0%,100% { transform: scaleY(0.4); } 50% { transform: scaleY(1); } }
          `}</style>
          <button
            type="button"
            onClick={openMomentsFeed}
            style={{
              position: 'fixed', right: '14px', bottom: 'calc(76px + env(safe-area-inset-bottom))',
              zIndex: 45,
              display: 'flex', alignItems: 'center', gap: '9px',
              padding: '12px 18px', borderRadius: '999px', border: 'none',
              background: 'linear-gradient(110deg, #f97316, #ec4899, #8b5cf6, #f97316)',
              backgroundSize: '300% 100%',
              animation: 'pfm-flow 7s linear infinite, pfm-glow 2.6s ease-in-out infinite',
              color: '#fff', fontFamily: "'Manrope', sans-serif",
              fontSize: '14px', fontWeight: 800, cursor: 'pointer',
            }}
          >
            <span style={{
              width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
              background: 'rgba(255,255,255,0.26)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Play size={12} fill="#fff" color="#fff" />
            </span>
            Moments
            <span style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '13px', flexShrink: 0 }}>
              {[0, 0.25, 0.5].map((d, i) => (
                <span key={i} style={{
                  width: '3px', height: '13px', borderRadius: '2px',
                  background: 'rgba(255,255,255,0.9)', transformOrigin: 'bottom',
                  animation: `pfm-eq 0.9s ease-in-out ${d}s infinite`,
                }} />
              ))}
            </span>
          </button>
        </>
      )}

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <nav style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '64px',
          backgroundColor: '#0a0a0f',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          zIndex: 50,
          paddingBottom: 'env(safe-area-inset-bottom)'
        }}>
          {[
            { id: 'home', icon: Home, label: 'Home' },
            { id: 'explore', icon: Grid, label: 'Explore' },
            { id: 'peak-moments', icon: Flame, label: 'Moments' },
            { id: 'favorites', icon: Heart, label: 'MySpace' },
            { id: 'votes', icon: Vote, label: 'Vote' },
            { id: 'forum', icon: MessageSquare, label: 'Forum' },
          ].map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            const color = item.id === 'peak-moments' ? '#F97316' : '#8B5CF6';
            return (
              <button
                key={item.id}
                onClick={() => navigateToTab(item.id)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '3px',
                  padding: '8px 4px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Icon style={{
                  width: '21px',
                  height: '21px',
                  color: isActive ? color : 'rgba(255,255,255,0.35)',
                  transition: 'color 0.15s'
                }} />
                <span style={{
                  fontSize: '9px',
                  color: isActive ? color : 'rgba(255,255,255,0.35)',
                  fontWeight: isActive ? '600' : '400',
                  transition: 'color 0.15s',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
};

export default ReactorHub;
